// Per-character driver: consumes InputFrame each tick and uses PD orientation torques
// (for spherical-jointed bones) + revolute motors (elbows/knees) to produce
// the floppy-but-trying active ragdoll behavior.

import type RAPIER from '@dimforge/rapier3d-compat';
import { CONFIG } from '../config';
import { InputBtn, type InputFrame, btn } from '../core/InputFrame';
import { Bone, BONE_COUNT, Ragdoll, quatFromAxisAngle, quatFromBasis, quatFromYaw, quatMul, QUAT_ID, type Quat } from './Ragdoll';
// CONFIG.sim.disableTorques is read inside step() — we re-import for the diagnostic toggle.
import { Grab } from './Grab';
import { type CharacterSnapshot, type FootstepEvent, type SplashEvent, type SwingStartEvent, emptySnapshot } from './SimState';

const C = CONFIG.character;

const WEDGE_CHECK_INTERVAL_TICKS = 24;   // 0.4 s at 60 Hz
const WEDGE_STRETCH_THRESHOLD = 0.30;    // metres — joint anchors > this apart = wedge
const WEDGE_DETECT_SEC = 0.8;            // continuous stretch this long → recover
const WEDGE_COOLDOWN_SEC = 1.2;          // post-pop suppression

export type SwingPhase = 'idle' | 'windup' | 'follow' | 'recover';
interface SwingState {
  phase: SwingPhase;
  t: number;
  side: 'L' | 'R';
}

// Continuous-stride foot positioning. Each foot tracks a pelvis-relative
// target every tick and is forcefully sprung to it:
//
//   forwardOffset = -cos(phase) · footSlotOffset · amplitude
//   lift          = stepLiftHeight · max(0, sin(phase))² · amplitude
//
// Phase advances by pelvis horizontal travel (`footGaitStride` metres per
// half-cycle), so stride tempo matches real movement speed. R foot is 180°
// out of phase with L. Amplitude (gaitActivity) smooths 0..1 so feet fade
// to REST when the character stops.
//
// `state` collapses to just plant | air — the old 'swing' was a by-product
// of slot snapping and is no longer needed. 'air' is set by doJump / KO
// paths; the foot controller skips the slot spring in that state so joint
// + gravity drive the jump arc, and a short raycast off the foot body
// detects landing.
type FootState = 'plant' | 'air';
interface Foot {
  side: 'L' | 'R';
  state: FootState;
  // Live target world pos (updated every tick). Read by hip-IK and
  // pelvis altitude PD via foot.plantX/Y/Z.
  plantX: number; plantY: number; plantZ: number;
  // Last frame's lift height — used to detect a landing edge for footstep
  // audio (prev > threshold, current ≤ threshold).
  prevLift: number;
  // Debounce between footstep sound emissions.
  stepCooldown: number;
}

export class Character {
  readonly id: number;
  readonly ragdoll: Ragdoll;
  readonly grab: Grab;
  /** Foot-plant events emitted during the current tick. World collects +
   *  clears these each step() so only the latest tick's landings ship in the
   *  snapshot. */
  readonly footsteps: FootstepEvent[] = [];
  /** Punch-start events emitted during the current tick (one per hand-press
   *  that actually initiates a swing). Same lifecycle as `footsteps`. */
  readonly swings: SwingStartEvent[] = [];
  /** Splash events emitted during the current tick — one on the rising edge
   *  of the swim state (gated on downward pelvis velocity so calm wade-ins
   *  stay silent), plus debounced stroke pulses while paddling. Same
   *  per-tick clear lifecycle as `footsteps` / `swings`. */
  readonly splashes: SplashEvent[] = [];

  swingL: SwingState = { phase: 'idle', t: 0, side: 'L' };
  swingR: SwingState = { phase: 'idle', t: 0, side: 'R' };
  // Headbutt (ground-only, same binding as jump-kick). Head is a qualifying
  // damage bone only while headbuttActive > 0 (the 'follow' window).
  headbuttPhase: 'idle' | 'windup' | 'follow' | 'recover' = 'idle';
  headbuttActive = 0;   // seconds of active 'follow' left — read by Combat
  private headbuttT = 0;
  private headbuttCooldown = 0;
  private targetYaw = 0;
  // Cached `Math.sin(targetYaw)` / `Math.cos(targetYaw)`. Refreshed by
  // `setTargetYaw()` on every assignment so any read can use the cache
  // directly. `step()` and its helpers consult these dozens of times per
  // tick (locomotion, grab, punch IK, hip IK) — caching means we pay the
  // trig cost once per actual yaw change, not once per consumer.
  private _yawSin = 0;
  private _yawCos = 1;
  private prevButtons = 0;
  // Per-hand punch cooldowns (seconds). Tick down each step.
  private punchCooldownL = 0;
  private punchCooldownR = 0;
  // Which arm went last. Next punch under a held Punch button always flips
  // to the opposite side → perfectly rhythmic alternation.
  private lastPunchSide: 'L' | 'R' = 'R';
  // Seconds since the last fired punch (or since the Punch button was
  // released). A held Punch fires one strike every `punchHoldIntervalSec`,
  // not whenever an arm happens to be idle — so the visible cadence is
  // metronomic instead of "two quick taps then wait".
  private punchHoldTimer = 0;
  // Feet alternate which one steps next when gait kicks off.
  // Idle pelvis anchor. Captured on the transition from moving → idle; the
  // hip-drive spring pulls the pelvis to this world XZ so we plant firmly
  // on tilted / moving surfaces instead of sliding off. null = not anchored
  // (we're actively moving or haven't stood still yet).
  private hipPlantX: number | null = null;
  private hipPlantZ = 0;
  private foot: { L: Foot; R: Foot };
  // Continuous gait phase in [0, 2π). Advances only while we're walking;
  // synced to foot swing state so the arms are actually in time with the legs.
  private gaitPhase = 0;
  private gaitActivity = 0; // 0..1 smoothed moving flag; fades arm amplitude in/out
  // Last sampled wish direction in world XZ (unit vector). Used to detect
  // sharp turns so we can kick the head forward into the new direction.
  // (0, 0) when the player isn't moving — next movement starts fresh.
  private headKickPrevDirX = 0;
  private headKickPrevDirZ = 0;
  // Jump-kick state. Fires once per air-time, re-armed when a foot replants.
  jumpKickActive = 0; // countdown timer; >0 means we're mid-strike
  private jumpKickUsed = false;
  // Upper body goes fully limp during a jump-kick so the legs drag the torso
  // along instead of the spine/altitude PD keeping us tall mid-strike.
  // Stays true until any bone touches a surface (arena/stairs/opponent).
  private jumpKickFloppy = false;
  // Health: two hidden bars. HP is long-term (10 KOs to deplete). KO is the
  // instant-knockout bar (like stamina; regens). See takeImpact for the
  // damage model + CONFIG.character knobs.
  hp = C.hpMax;
  ko = C.koMax;
  koStunRemaining = 0;          // seconds of flopped stun; can't be re-KO'd while >0
  wasted = false;                // HP hit 0 → finished, treated like ringedOut
  private koRegenWait = 0;
  private koGracePostStand = 0;  // post-stand immunity window — HP/KO ignore hits briefly
  stamina = 1;
  // Stamina regen is delayed briefly after any spend — but if a spend drains
  // to EXACTLY zero, regen starts immediately (that's the "crash & recover"
  // punishment for over-spending). Locked = true gates all spending actions
  // until stamina refills to full.
  private staminaRegenWait = 0;
  // Public so tutorial / sandbox behaviors can clear it when pinning the
  // pool full each tick — Character only auto-clears when stamina regens
  // up to 1 (see step()), so an external write to `stamina` doesn't on its
  // own release the lockout.
  staminaLocked = false;
  stunRemaining = 0;
  knockedOut = false;
  ringedOut = false;
  // Wriggle: victim-side bar that depletes while being grabbed. Drain is
  // mashed down by jump presses, punch triggers, and sharp wish-dir changes.
  // Hits 0 → all attackers' grabs on us are forcibly released + a 1s lock
  // prevents any attacker's SECOND hand from re-latching immediately.
  wriggle = CONFIG.character.grab.wriggleMax;
  postWriggleLock = 0;
  /** Attackers that currently have ≥1 hand on us. */
  private attackerCount = 0;
  private prevWishX = 0;
  private prevWishZ = 0;
  /** Set by step() when the wriggle bar hits 0; World reads + clears it,
   *  forcing every attacker's Grab to release all joints on this victim. */
  wriggleEscapeRequested = false;
  /** Seconds remaining of the post-grab "unlock" window — while > 0 we
   *  suppress the idle pelvis-follow anchor so a throw actually carries us
   *  instead of the standing PDs snapping us back in place. */
  private postGrabUnlock = 0;

  /** Surface-grab pull-up state machine. While double-surface-grabbed and
   *  Jump is held, the pelvis is winched upward for a fixed `wind` window
   *  before everything launches and the surface joints release. Held-button
   *  driven so the player can chain pull-ups against a wall to climb it. */
  private pullupPhase: 'idle' | 'wind' | 'release' = 'idle';
  private pullupT = 0;

  /** World-Y of the map's water surface at a given (x, z), or null on dry
   *  maps. For flat-water maps this is a constant; on maps with a wave
   *  field (sinking-ship's ocean) this evaluates the wave displacement at
   *  the sample point so submersion + buoyancy track the live surface
   *  rather than a flat plane. When null the swim mechanic is permanently
   *  off — every check that would gate on `isSwimming` short-circuits to
   *  `false`. */
  private readonly getSurfaceY: ((x: number, z: number) => number) | null;
  /** Cached this-tick swim state. Read by other systems (arm targeting,
   *  stroke debounce) so the submersion sweep only runs once per step. */
  private isSwimming = false;
  private prevIsSwimming = false;
  /** Previous-tick `inWater` (50%-submersion bool). Used for the rising-
   *  edge entry-splash so KO'd / thrown bodies splash on entry too — the
   *  old `prevIsSwimming` rising edge missed those because their KO
   *  state suppressed `isSwimming`. */
  private prevInWater = false;
  /** Free-running clock used to phase the arm flail (sin / cos at
   *  flailFreqHz) so it doesn't restart from zero on each swim entry. */
  private swimClock = 0;
  /** Debounce on per-stroke splash SFX. Counts up while swimming + moving;
   *  fires + resets when it crosses `strokeSplashIntervalSec`. */
  private strokeSplashTimer = 0;

  /** Tick counter for stuck detection — runs the joint-stretch check every
   *  WEDGE_CHECK_INTERVAL_TICKS ticks (0.4 s at 60 Hz). */
  private wedgeCheckTicks = 0;
  /** Seconds of continuously-detected joint stretch. Crosses
   *  WEDGE_DETECT_SEC → fire the safe-Y-pop. */
  private wedgeStuckTime = 0;
  /** Post-recovery suppression window. Decremented every step; detection is
   *  inhibited while > 0 so we don't fire twice on the same wedge if the
   *  lift didn't fully resolve it. */
  private wedgeCooldown = 0;

  /** When false, the joint-stretch / buried-bone wedge detector skips its
   *  pop-to-topmost-surface recovery. The tutorial disables it so players
   *  experiencing a stuck limb learn to dislodge rather than being teleported. */
  private readonly wedgeAutoResolveEnabled: boolean;

  constructor(
    id: number,
    ragdoll: Ragdoll,
    grab: Grab,
    initialYaw: number,
    getSurfaceY: ((x: number, z: number) => number) | null = null,
    wedgeAutoResolveEnabled: boolean = true
  ) {
    this.id = id;
    this.ragdoll = ragdoll;
    this.grab = grab;
    this.setTargetYaw(initialYaw);
    this.getSurfaceY = getSurfaceY;
    this.wedgeAutoResolveEnabled = wedgeAutoResolveEnabled;
    this.foot = {
      L: makeFoot('L'),
      R: makeFoot('R')
    };
    this.plantFeetHere();
  }

  /** Character facing yaw — read by Combat for accuracy computation. */
  get yaw(): number { return this.targetYaw; }

  /** Single point of mutation for `targetYaw` so the sin/cos cache stays
   *  coherent. Every `this.targetYaw = ...` site in the class funnels
   *  through here. The trig cost is paid exactly once per actual yaw
   *  change, regardless of how many consumers read it this tick. */
  private setTargetYaw(y: number): void {
    this.targetYaw = y;
    this._yawSin = Math.sin(y);
    this._yawCos = Math.cos(y);
  }

  /** True while any attacker has ≥1 hand on us. Drives the wriggle bar +
   *  is the "suspend airborne detection" flag for systems that shouldn't
   *  treat lift-by-grab as a real jump/fall. */
  get beingGrabbed(): boolean { return this.attackerCount > 0; }

  /** Attacker-side callback: one of our bones just got a joint attached.
   *  Grab.update fires these on attach/detach transitions. */
  onAttackerAttached(): void { this.attackerCount += 1; }
  onAttackerReleased(): void {
    this.attackerCount = Math.max(0, this.attackerCount - 1);
    if (this.attackerCount === 0) {
      // Instant reset on release (user preference) — fresh wriggle bar for
      // the next grab instead of slow regen.
      this.wriggle = CONFIG.character.grab.wriggleMax;
      // Open a brief "don't snap me back" window so a throw carries.
      this.postGrabUnlock = CONFIG.character.grab.postGrabUnlockSec;
    }
  }

  /** Initial / respawn foot state. Seeds the target from the current
   *  lower-leg body position so the first-tick spring has nothing to pull
   *  against; updateFeet will then track the live continuous stride. */
  private plantFeetHere(): void {
    for (const side of ['L', 'R'] as const) {
      const body = this.ragdoll.body(side === 'L' ? Bone.LowerLegL : Bone.LowerLegR);
      const t = body.translation();
      const f = this.foot[side];
      f.state = 'plant';
      f.plantX = t.x; f.plantY = t.y; f.plantZ = t.z;
      f.prevLift = 0;
    }
  }

  step(input: InputFrame, dt: number): void {
    const pelvis = this.ragdoll.pelvis();

    if (this.stunRemaining > 0) this.stunRemaining = Math.max(0, this.stunRemaining - dt);

    // ---- KO stun / regen ----
    // While koStunRemaining > 0 the character is fully limp (knockedOut=true)
    // and immune to further damage. On timer expiry we stand up, reset the KO
    // bar, and set a short grace window so lingering bodies can't instantly
    // re-KO. Skip the stand-up path if the character has been ringed-out or
    // wasted in the meantime.
    if (this.koStunRemaining > 0) {
      this.koStunRemaining = Math.max(0, this.koStunRemaining - dt);
      if (this.koStunRemaining <= 0 && !this.ringedOut && !this.wasted) {
        this.ko = C.koMax;
        this.knockedOut = false;
        this.koGracePostStand = C.koGracePostStandSec;
        this.koRegenWait = 0;
      }
    } else {
      if (this.koGracePostStand > 0) this.koGracePostStand = Math.max(0, this.koGracePostStand - dt);
      if (this.koRegenWait > 0) {
        this.koRegenWait = Math.max(0, this.koRegenWait - dt);
      } else if (this.ko < C.koMax) {
        this.ko = Math.min(C.koMax, this.ko + dt * C.koRegenPerSec);
      }
    }

    const stunned = this.stunRemaining > 0 || this.knockedOut;
    const stiffMul = this.knockedOut ? 0 : (stunned ? 0.18 : 1.0);

    // Per-hand cooldowns + jump-kick/headbutt timer advance regardless.
    if (this.punchCooldownL > 0) this.punchCooldownL = Math.max(0, this.punchCooldownL - dt);
    if (this.punchCooldownR > 0) this.punchCooldownR = Math.max(0, this.punchCooldownR - dt);
    if (this.jumpKickActive > 0) this.jumpKickActive = Math.max(0, this.jumpKickActive - dt);
    if (this.headbuttCooldown > 0) this.headbuttCooldown = Math.max(0, this.headbuttCooldown - dt);
    // Re-arm jump-kick the instant any foot replants.
    if (this.foot.L.state === 'plant' || this.foot.R.state === 'plant') this.jumpKickUsed = false;

    // ---- Submersion sweep ----
    // Mass-weighted submersion fraction against the map's water surface
    // (null → dry map → swim path stays dead). Each bone is compared to
    // the surface sampled at its own (x, z) so a wave crest under one bone
    // and a trough under another are honoured independently. Computed
    // BEFORE the floppy / upperLimp check so a jump-kick into water can
    // count "hitting the water" as contact and exit floppy mode.
    this.swimClock += dt;
    let submergedFrac = 0;
    if (this.getSurfaceY !== null) {
      let below = 0, total = 0;
      for (const b of this.ragdoll.handles.bodies) {
        const m = b.mass();
        total += m;
        const t = b.translation();
        if (t.y < this.getSurfaceY(t.x, t.z)) below += m;
      }
      submergedFrac = total > 0 ? below / total : 0;
    }
    const inWater = this.getSurfaceY !== null && submergedFrac > 0.5;

    // Exit floppy mode once ANY body part makes contact with the world —
    // feet replanting, any bone within a sliver of a surface, OR landing
    // in water. Without the water exit, jump-kicking into the ocean
    // leaves the body stuck in floppy state forever (no buoyancy
    // applied, character "falls through" the surface).
    if (this.jumpKickFloppy) {
      const anyPlanted = this.foot.L.state === 'plant' || this.foot.R.state === 'plant';
      if (anyPlanted || this.anyBoneNearGround(0.12) || inWater) this.jumpKickFloppy = false;
    }
    const upperLimp = this.jumpKickFloppy || this.knockedOut;

    // ---- Swim active state ----
    // Active swim mode (input-driven locomotion + arm flail + stroke
    // splashes + stamina drain) requires consciousness. KO / jump-kick
    // floppy bodies still get buoyancy + drag + current applied below —
    // they just float / drift instead of swimming.
    this.prevIsSwimming = this.isSwimming;
    this.isSwimming =
      inWater
      && this.grab.count() === 0
      && !this.knockedOut
      && !this.jumpKickFloppy;

    // Entry splash on rising edge of `inWater` — fires for thrown
    // victims and KO'd bodies tumbling in too, not just for conscious
    // swimmers. Still gated on impact velocity so a calm wade-in or a
    // slowly-rising water surface catching up to a stationary character
    // stays silent.
    if (inWater && !this.prevInWater) {
      const pv = pelvis.linvel();
      const downVel = -pv.y;
      if (downVel >= C.swim.entrySplashMinDownVel) {
        const pt = pelvis.translation();
        if (Number.isFinite(pt.x) && Number.isFinite(pt.z)) {
          this.splashes.push({ characterId: this.id, x: pt.x, y: this.getSurfaceY!(pt.x, pt.z), z: pt.z });
        }
      }
      this.strokeSplashTimer = 0;
    }
    this.prevInWater = inWater;
    if (!this.isSwimming) this.strokeSplashTimer = 0;

    // Drop the idle pelvis anchor only on full limp (KO / jump-kick) so that
    // post-recovery we capture a fresh anchor at the new position. A brief
    // stun must NOT clear it — otherwise the idle branch just recaptures the
    // anchor each tick at the currently-sliding pelvis, which looks like
    // the character is drifting with no friction.
    if (this.postGrabUnlock > 0) {
      // Auto-terminate the unlock as soon as the thrown-but-standing victim
      // actually lands — otherwise the hip-plant anchor stays off too long
      // and they slide weirdly on the ground for the rest of the window.
      // `foot.state` isn't reliable here: it stays 'plant' even while the
      // victim is lifted (only jump/void explicitly flip it). Use the same
      // surface-relative grounded check that gates jumping: pelvis close
      // enough above the lowest planted foot to count as standing.
      if (!upperLimp) {
        let minPlantY = Infinity;
        if (this.foot.L.state === 'plant') minPlantY = Math.min(minPlantY, this.foot.L.plantY);
        if (this.foot.R.state === 'plant') minPlantY = Math.min(minPlantY, this.foot.R.plantY);
        const pY = pelvis.translation().y;
        const grounded = Number.isFinite(minPlantY) && (pY - minPlantY) < C.pelvisAboveFoot + 0.15;
        if (grounded) this.postGrabUnlock = 0;
        else this.postGrabUnlock = Math.max(0, this.postGrabUnlock - dt);
      } else {
        this.postGrabUnlock = Math.max(0, this.postGrabUnlock - dt);
      }
    }
    // Clear the idle pelvis-anchor whenever the upper body is limp OR we're
    // currently being held OR just been released (postGrabUnlock > 0).
    // `applyPelvisFollow` would otherwise spring the pelvis back to where we
    // stood, killing the throw's forward velocity before it could carry us.
    if (upperLimp || this.beingGrabbed || this.postGrabUnlock > 0) this.hipPlantX = null;

    // Sprint is suppressed underwater. Foot-plant locomotion is bypassed
    // while swimming so a held Shift would otherwise just burn stamina
    // without any speed benefit.
    const sprintOn = !stunned && !this.staminaLocked && !this.isSwimming && btn(input, InputBtn.Sprint);

    // ---- Pelvis altitude PD ("trying to stand") ----
    // Target is relative to the LOWEST planted foot so the pelvis rides up
    // stairs as the supporting foot climbs. If no foot is planted (jump / KO)
    // we fall back to a fixed world-Y target — but only when a ground ray
    // from the pelvis actually hits something within `voidProbeDist`. Walking
    // off the arena edge lands both feet in 'air' AND puts the pelvis over
    // empty space, and without this guard the fallback PD would pin the
    // character hovering in mid-air above the lava instead of letting them
    // fall. Disabled during jump-kick so the torso actually falls too.
    if (!upperLimp && !this.isSwimming) {
      const t = pelvis.translation();
      const v = pelvis.linvel();
      let minPlantY = Infinity;
      if (this.foot.L.state === 'plant') minPlantY = Math.min(minPlantY, this.foot.L.plantY);
      if (this.foot.R.state === 'plant') minPlantY = Math.min(minPlantY, this.foot.R.plantY);

      let applyStand = true;
      let targetY = 0;
      if (Number.isFinite(minPlantY)) {
        targetY = minPlantY + C.pelvisAboveFoot;
      } else {
        // No foot planted — probe for ground directly below the pelvis.
        // Threshold is generous (4 m) so a tall jump over the canvas still
        // reads as "over ground"; stepping off the edge reads as "over void".
        const voidProbeDist = 4.0;
        const groundBelow = this.ragdoll.castGroundRay(t.x, t.y, t.z, voidProbeDist);
        if (groundBelow == null) {
          applyStand = false;
        } else {
          // Target is a FIXED OFFSET above whatever ground we're over — not a
          // world-absolute Y. With a world-absolute target the pelvis would
          // pin at y = 1.21 regardless of the actual floor below (float walk
          // over tilted / elevated surfaces); tracking the ray hit puts the
          // pelvis at a consistent standing height above the real floor.
          const groundY = t.y - groundBelow;
          targetY = groundY + C.footCenterHeight + C.pelvisAboveFoot;
        }
      }

      if (applyStand) {
        const heightErr = targetY - t.y;
        const upForce = clamp(heightErr * C.standKp - v.y * C.standKd, 0, C.standMax) * pelvis.mass() * (stunned ? 0.4 : 1);
        pelvis.applyImpulse({ x: 0, y: upForce * dt, z: 0 }, true);
      }
    }

    // ---- Planar movement: feet drive locomotion ----
    // Also suppressed during jump-kick floppy so you can't steer / rejump /
    // re-trigger while your body is still mid-fall.
    let wishX = 0, wishZ = 0, wishMag = 0;
    if (!stunned && !this.jumpKickFloppy) {
      const yawSin = Math.sin(input.aimYaw);
      const yawCos = Math.cos(input.aimYaw);
      wishX = -input.moveZ * yawSin - input.moveX * yawCos;
      wishZ = -input.moveZ * yawCos + input.moveX * yawSin;
      wishMag = Math.hypot(wishX, wishZ);
      if (wishMag > 0.001) {
        this.setTargetYaw(Math.atan2(wishX / wishMag, wishZ / wishMag));
      }
      const justJumped = btn(input, InputBtn.Jump) && !(this.prevButtons & InputBtn.Jump);
      if (justJumped && !this.staminaLocked && !this.grab.isDoubleSurfaceHolding()) {
        // Defer to the pull-up state machine when both hands are on surfaces —
        // even if we happen to still have feet on the ground (e.g. holding a
        // low rope). The pull-up driver runs after grab.update and reads the
        // held-button state directly, so chained pulls work without edges.
        //
        // Grounded check is surface-relative, not world-Y absolute: we need
        // a planted foot AND the pelvis within one standing-height of that
        // foot. Previously the absolute check (`pelvis.y < 0.86`) refused
        // jumps on any elevated surface — stairs, ships, rocks — which was
        // the core bug the user saw.
        let minPlantY = Infinity;
        if (this.foot.L.state === 'plant') minPlantY = Math.min(minPlantY, this.foot.L.plantY);
        if (this.foot.R.state === 'plant') minPlantY = Math.min(minPlantY, this.foot.R.plantY);
        const grounded = Number.isFinite(minPlantY) && (pelvis.translation().y - minPlantY) < C.pelvisAboveFoot + 0.15;
        // Swim-jump: same Δv as a normal jump. Lets a player lurch upward
        // out of water (or get a head start on the climb-out grab).
        if (grounded || this.isSwimming) this.doJump();
      }

      // Jump-kick / headbutt share the same binding. Both-air → jumpkick
      // (one-shot per airtime, re-armed on replant). Otherwise (both planted
      // or mid-stride) → headbutt, gated by its own cooldown + stamina.
      // While swimming both are suppressed — only swim, jump, grab.
      const jumpKickPressed = btn(input, InputBtn.JumpKick) && !(this.prevButtons & InputBtn.JumpKick);
      const bothAir = this.foot.L.state === 'air' && this.foot.R.state === 'air';
      if (jumpKickPressed && !this.staminaLocked && !this.isSwimming) {
        if (bothAir && !this.jumpKickUsed) {
          this.triggerJumpKick();
        } else if (!bothAir && this.headbuttPhase === 'idle' && this.headbuttCooldown <= 0) {
          this.triggerHeadbutt();
        }
      }

      // Mid-air control: small horizontal nudge on pelvis. Legs follow via IK.
      if (bothAir && wishMag > 0.001) {
        const inv = 1 / wishMag;
        const m = pelvis.mass();
        const a = C.airControlAccel * dt * m;
        pelvis.applyImpulse({ x: wishX * inv * a, y: 0, z: wishZ * inv * a }, true);
      }

      // Sprint burn: only while actually moving and sprint held.
      if (sprintOn && wishMag > 0.08) {
        this.spendStamina(C.staminaSprintDrainPerSec * dt);
      }
    }
    if (!this.knockedOut) this.updateFeet(dt);
    // Pelvis-over-feet follow is part of "staying upright" — skip it while
    // the upper body is meant to be limp.
    // Skip the idle XZ anchor spring while being grabbed, and for a short
    // window after release (postGrabUnlock) so a throw's forward velocity
    // actually carries the victim.
    // While swimming we also skip the pelvis-follow anchor: the swim
    // locomotion impulse below is the only thing that should drive
    // horizontal motion in water, and the standing anchor would fight it.
    if (!upperLimp && !this.beingGrabbed && this.postGrabUnlock <= 0 && !this.isSwimming) {
      this.applyPelvisFollow(wishX, wishZ, wishMag, sprintOn, dt);
    }
    // ---- Water physics ----
    // Layers, each gated differently:
    //   FLOPPED-BODY BUOYANCY (KO / wasted / jump-kick-floppy) runs
    //     per-bone whenever the bone dips below its float target — NOT
    //     gated on the `inWater` mass threshold. Without this, a corpse
    //     whose centre of mass equilibrates near the waterline flickers
    //     across the >50%-submerged threshold, the buoyancy block turns
    //     off in those windows, and gravity drags the body deeper each
    //     cycle until it sinks. The per-bone gate handles "out of water"
    //     correctly on its own (bones above target → no force).
    //   CONSCIOUS-SWIMMER BUOYANCY (head/torso/pelvis with bob) keeps
    //     its `inWater` gate so a wading walker doesn't get yanked.
    //   DRAG + CURRENT run per-bone whenever any bone is below its
    //     local surface — applies to flopped corpses (so they drift
    //     with the current too) and to live swimmers alike.
    //   SWIM LOCOMOTION + STROKE SFX + STAMINA DRAIN are conscious-only
    //     (`isSwimming`). Stamina hitting 0 drowns: hp=0, wasted=true,
    //     knockedOut=true → next tick falls into the flopped-float
    //     branch above and floats permanently.
    if (this.getSurfaceY !== null) {
      const Sw = C.swim;
      const sampleSurfaceY = this.getSurfaceY;
      const flopped = upperLimp || this.wasted;

      // --- Buoyancy ---
      if (flopped) {
        // Float-flat: every bone targets the wave surface at its own
        // (x, z) plus a fixed lift, so all limbs ride above the wave
        // with no head/limb bobbing on top of the wave motion. Used for
        // KO'd / wasted / jump-kick-floppy bodies. Per-bone, runs every
        // tick with no `inWater` gate — see comment above.
        const off = Sw.koLimbFloatOffset;
        for (const b of this.ragdoll.handles.bodies) {
          const t = b.translation();
          const v = b.linvel();
          const targetY = sampleSurfaceY(t.x, t.z) + off;
          const depth = targetY - t.y;
          if (depth <= 0) continue;
          const upAccel = Math.min(
            depth * Sw.buoyancyKp - v.y * Sw.buoyancyKd,
            Sw.buoyancyMaxAccel
          );
          if (upAccel > 0) {
            b.applyImpulse({ x: 0, y: upAccel * b.mass() * dt, z: 0 }, true);
          }
        }
      } else if (inWater) {
        // Conscious swimmer — head/torso/pelvis with bob; legs trail.
        // Bob: oscillate target offset downward by `bobAmp * (0.5 + 0.5 *
        // sin(2π·f·t + phase))`. Phase per-character so multiple swimmers
        // don't bob in lockstep.
        const phase = this.id * 1.357;
        const bobNorm = 0.5 + 0.5 * Math.sin(this.swimClock * Sw.bobFreqHz * 2 * Math.PI + phase);
        const bobOffset = -Sw.bobAmp * bobNorm;
        const buoy = (bi: Bone, off: number): void => {
          const b = this.ragdoll.body(bi);
          const t = b.translation();
          const v = b.linvel();
          const targetY = sampleSurfaceY(t.x, t.z) + off + bobOffset;
          const depth = targetY - t.y;
          if (depth <= 0) return;
          const upAccel = Math.min(
            depth * Sw.buoyancyKp - v.y * Sw.buoyancyKd,
            Sw.buoyancyMaxAccel
          );
          if (upAccel > 0) {
            b.applyImpulse({ x: 0, y: upAccel * b.mass() * dt, z: 0 }, true);
          }
        };
        buoy(Bone.Head, Sw.buoyancyHeadOffsetY);
        buoy(Bone.Torso, Sw.buoyancyTorsoOffsetY);
        buoy(Bone.Pelvis, Sw.buoyancyPelvisOffsetY);
      }

      // --- Drag + current ---
      // Per-submerged-bone horizontal: drag pulls velocity toward zero,
      // current adds a constant directional acceleration. Both scale by
      // mass so total force on the body is uniform, not biased by limb
      // sizes. Per-bone gate (`t.y >= surfaceY` → skip) handles bodies
      // out of water with no extra outer condition.
      const dragK = Sw.linearDrag;
      const cur = Sw.current;
      const curLen = Math.hypot(cur.dirX, cur.dirZ);
      const cdx = curLen > 1e-6 ? (cur.dirX / curLen) * cur.strength : 0;
      const cdz = curLen > 1e-6 ? (cur.dirZ / curLen) * cur.strength : 0;
      for (const b of this.ragdoll.handles.bodies) {
        const t = b.translation();
        if (t.y >= sampleSurfaceY(t.x, t.z)) continue;
        const v = b.linvel();
        const m = b.mass();
        b.applyImpulse(
          {
            x: (-v.x * dragK + cdx) * m * dt,
            y: 0,
            z: (-v.z * dragK + cdz) * m * dt
          },
          true
        );
      }

      // --- Conscious-swimmer-only: locomotion, splashes, stamina drain ---
      if (this.isSwimming) {
        // Swim locomotion: pelvis horizontal impulse from input wish
        // vector. Same shape as mid-air control, but with a larger
        // acceleration tuned for water — and tuned to just overpower
        // the current so swimming upstream is barely possible.
        if (wishMag > 0.001) {
          const inv = 1 / wishMag;
          const m = pelvis.mass();
          const a = Sw.swimAccel * dt * m;
          pelvis.applyImpulse({ x: wishX * inv * a, y: 0, z: wishZ * inv * a }, true);
        }
        // Per-stroke splash. Debounced — fires every
        // strokeSplashIntervalSec while paddling.
        if (wishMag > 0.08) {
          this.strokeSplashTimer += dt;
          if (this.strokeSplashTimer >= Sw.strokeSplashIntervalSec) {
            this.strokeSplashTimer = 0;
            const pt = pelvis.translation();
            if (Number.isFinite(pt.x) && Number.isFinite(pt.z)) {
              this.splashes.push({ characterId: this.id, x: pt.x, y: sampleSurfaceY(pt.x, pt.z), z: pt.z });
            }
          }
        } else {
          this.strokeSplashTimer = 0;
        }

        // Stamina drain while swimming. Empty stamina underwater = drown:
        // hp drops to 0, body is wasted + KO'd, and the float-flat
        // buoyancy branch above will float the corpse permanently.
        this.spendStamina(Sw.staminaDrainPerSec * dt);
        if (this.stamina <= 0 && !this.wasted) {
          this.hp = 0;
          this.wasted = true;
          this.knockedOut = true;
          this.koStunRemaining = Sw.drownStunSec;
        }
      }
    }
    this.updateGait(dt);

    // ---- Pose-holding via PD orientation torques ----
    // Diagnostic: when CONFIG.sim.disableTorques is true, skip all PD torques
    // and let the body hang on its joints alone.
    if (CONFIG.sim.disableTorques) {
      if (!stunned) {
        this.tickSwing(this.swingL, dt);
        this.tickSwing(this.swingR, dt);
        const grabDiag = btn(input, InputBtn.Grab);
        const fwdX = this._yawSin;
        const fwdZ = this._yawCos;
        const punchDiag = btn(input, InputBtn.Punch);
        this.grab.update(grabDiag, grabDiag, fwdX, fwdZ, punchDiag, dt);
      } else {
        this.grab.releaseAll();
      }
      this.stamina = Math.min(1, this.stamina + dt * 0.18);
      this.prevButtons = input.buttons;
      return;
    }

    const facing = quatFromYaw(this.targetYaw);

    // Upper-body pose torques are the main thing keeping us vertical — gated
    // off during jump-kick so the torso gets dragged along by the launched
    // legs instead of floating tall behind them.
    if (!upperLimp) {
      this.ragdoll.applyOrientationTorque(Bone.Pelvis, facing, C.spineStiff * stiffMul * 1.4, C.spineDamp * 1.2, dt);
      // Sprint leans the torso forward slightly (scaled by gait activity so
      // it fades in during the run-up). +X-axis rotation tilts top toward
      // +Z = the character's forward. Walk keeps facing upright.
      const torsoPitch = sprintOn ? +C.torsoSprintLean * this.gaitActivity : 0;
      const torsoTarget = torsoPitch !== 0
        ? quatMul(facing, quatFromAxisAngle({ x: 1, y: 0, z: 0 }, torsoPitch))
        : facing;
      this.ragdoll.applyOrientationTorque(Bone.Torso, torsoTarget, C.torsoStiff * stiffMul, C.torsoDamp, dt);
      this.ragdoll.applyOrientationTorque(Bone.Head, facing, C.neckStiff * stiffMul, C.neckDamp, dt);
      // Gentle forward pull during gait — head gets tugged ahead of the
      // torso like there's momentum pulling it. Applied as a horizontal
      // impulse so the PD keeps the head upright; the spherical joint at
      // the BOTTOM of the head lets a little pitch bleed through, which is
      // the "effective neck" look.
      const pullSign = C.headPullBackward ? -1 : +1;
      if (this.gaitActivity > 0.02) {
        const head = this.ragdoll.body(Bone.Head);
        const m = head.mass();
        const sprintScale = sprintOn ? C.headForwardPullSprintMul : 1;
        const pull = C.headForwardPull * sprintScale * this.gaitActivity * pullSign;
        const fwdX = this._yawSin;
        const fwdZ = this._yawCos;
        head.applyImpulse({ x: fwdX * pull * m * dt, y: 0, z: fwdZ * pull * m * dt }, true);
      }
      // One-shot head kick on sharp direction changes. The continuous pull
      // above has too much inertia to catch up on a quick turn — especially
      // at sprint, where the body is moving faster than the pull can drag
      // the head forward. Snap it into the new heading instead.
      if (wishMag > 0.08) {
        const wx = wishX / wishMag;
        const wz = wishZ / wishMag;
        const prevLen = Math.hypot(this.headKickPrevDirX, this.headKickPrevDirZ);
        if (prevLen > 0.5) {
          const dot = Math.max(-1, Math.min(1, wx * this.headKickPrevDirX + wz * this.headKickPrevDirZ));
          const angle = Math.acos(dot);
          const threshold = C.headKickAngleDeg * Math.PI / 180;
          if (angle > threshold) {
            const head = this.ragdoll.body(Bone.Head);
            const m = head.mass();
            const dv = (sprintOn ? C.headKickRunDv : C.headKickWalkDv) * pullSign;
            head.applyImpulse({ x: wx * dv * m, y: 0, z: wz * dv * m }, true);
            this.headKickPrevDirX = wx;
            this.headKickPrevDirZ = wz;
          }
        } else {
          this.headKickPrevDirX = wx;
          this.headKickPrevDirZ = wz;
        }
      } else {
        this.headKickPrevDirX = 0;
        this.headKickPrevDirZ = 0;
      }
    }

    // ---- Arms: per-side targets. Priority: punch > grab > gait > idle ----
    // Single Grab button engages BOTH arms — no more per-hand grab control.
    // Still gated by stamina lockout — once drained, no new grabs (in-flight
    // grabs get released in the grab.update call).
    //
    // Exception: if one hand is already grabbing AND Punch is held, we
    // suppress the free hand's grab intent so the free hand is routed
    // through the punch system instead (see grab.update's punchHeld gate).
    const grabBtn = !stunned && !this.staminaLocked && btn(input, InputBtn.Grab);
    // Punch is suppressed while swimming — only swim, jump, grab.
    const punchBtn = !stunned && !this.staminaLocked && !this.isSwimming && btn(input, InputBtn.Punch);
    // Per-hand intent: if a hand is already stuck, it keeps grabbing. If
    // the free hand would grab but Punch is held, don't reach (reserve it
    // for the punch system). Otherwise grab if the Grab button is down.
    const stuckL = this.grab.isHandStuck('L');
    const stuckR = this.grab.isHandStuck('R');
    const grabL = grabBtn && (stuckL || !(this.grab.count() >= 1 && punchBtn && !stuckL));
    const grabR = grabBtn && (stuckR || !(this.grab.count() >= 1 && punchBtn && !stuckR));
    // Ipsilateral arm stride with asymmetric amps. L arm swings FORWARD
    // during L-leg swing (sin(phase) > 0) and BACK during R-leg swing
    // (sin(phase) < 0); R arm is the opposite phase. Back amplitude > forward
    // so each backswing reads as a wind-up into the next stride. Negative
    // angle = forward in this codebase's +X-rotation convention.
    const fwdA  = sprintOn ? C.armSwingFwdSprint  : C.armSwingFwd;
    const backA = sprintOn ? C.armSwingBackSprint : C.armSwingBack;
    const sinP = Math.sin(this.gaitPhase);
    const gate = this.gaitActivity;
    let armLAngle: number, armRAngle: number;
    if (sinP >= 0) {
      armLAngle = -fwdA  * sinP * gate; // L leg swing → L arm forward
      armRAngle = +backA * sinP * gate; // R arm wind-up back
    } else {
      armLAngle = -backA * sinP * gate; // L arm wind-up back
      armRAngle = +fwdA  * sinP * gate; // R leg swing → R arm forward
    }
    const swingingL = isSwingActive(this.swingL);
    const swingingR = isSwingActive(this.swingR);
    // Flay sign: positive Z-rotation sends the arm's hand toward +X
    // (character's right). L arm is on the character's left, so it flays
    // outward with NEGATIVE angle; R arm flays outward with POSITIVE.
    // Grab arm-aim quat: when this hand is reaching for (or stuck on) a
    // target, orient the upper arm so its +Y bone axis points from the
    // shoulder toward the target body. Falls back to the old "straight
    // forward (−π/2 around X)" pose when no target is found.
    const doubleOnSameVictim = this.grab.isDoubleHolding();
    const grabAimL = this.computeGrabArmQuat('L', facing, doubleOnSameVictim);
    const grabAimR = this.computeGrabArmQuat('R', facing, doubleOnSameVictim);
    // Swim flail: alternating freestyle stroke. Each arm is aimed via a
    // (yawAim, pitch) factorisation just like grabAim — pitch = -π/2 is
    // horizontal forward, more negative lifts UP, less negative drops
    // toward the water. The stroke uses max(0, sin) so each arm reaches
    // up only on its own half of the cycle, dropping back to horizontal
    // forward on the recovery half. A small outward yaw flay keeps the
    // two arms from stroking through each other, and the amplitude is
    // gated by gaitActivity so a stationary swimmer holds both arms
    // outstretched ahead instead of windmilling in place.
    let swimAimL: Quat | null = null;
    let swimAimR: Quat | null = null;
    if (this.isSwimming) {
      const Sw = C.swim;
      const phase = this.swimClock * (2 * Math.PI * Sw.flailFreqHz);
      const moveAmp = this.gaitActivity; // 0..1, smoothed in updateGait
      const reachL = Math.max(0, Math.sin(phase)) * moveAmp;
      const reachR = Math.max(0, Math.sin(phase + Math.PI)) * moveAmp;
      const pitchL = -Math.PI * 0.5 - reachL * Sw.flailUpAmp;
      const pitchR = -Math.PI * 0.5 - reachR * Sw.flailUpAmp;
      const yawL = this.targetYaw - Sw.flailSideFlay;
      const yawR = this.targetYaw + Sw.flailSideFlay;
      swimAimL = quatMul(quatFromYaw(yawL), quatFromAxisAngle({ x: 1, y: 0, z: 0 }, pitchL));
      swimAimR = quatMul(quatFromYaw(yawR), quatFromAxisAngle({ x: 1, y: 0, z: 0 }, pitchR));
    }
    const armL = this.armTargetPrioritised(this.swingL, facing, grabL, armLAngle, -C.armFlayAngle, grabAimL, swimAimL);
    const armR = this.armTargetPrioritised(this.swingR, facing, grabR, armRAngle, +C.armFlayAngle, grabAimR, swimAimR);
    // Lighter PD when doing gait sway so the arms read as loose, not locked.
    // Sprint goes looser still (armSwingStiffSprint < armSwingStiff) so the
    // arms whip and trail through the bigger sprint amps.
    const gaitDrivingL = !swingingL && !grabL && this.swingL.phase === 'idle' && this.gaitActivity > 0.05;
    const gaitDrivingR = !swingingR && !grabR && this.swingR.phase === 'idle' && this.gaitActivity > 0.05;
    const gaitStiff = sprintOn ? C.armSwingStiffSprint : C.armSwingStiff;
    const gaitDamp  = sprintOn ? C.armSwingDampSprint  : C.armSwingDamp;
    const stiffArmL = swingingL ? C.swingStiff : (gaitDrivingL ? gaitStiff : C.shoulderStiff * stiffMul);
    const dampArmL  = swingingL ? C.swingDamp  : (gaitDrivingL ? gaitDamp  : C.shoulderDamp);
    const stiffArmR = swingingR ? C.swingStiff : (gaitDrivingR ? gaitStiff : C.shoulderStiff * stiffMul);
    const dampArmR  = swingingR ? C.swingDamp  : (gaitDrivingR ? gaitDamp  : C.shoulderDamp);
    if (!upperLimp) {
      this.ragdoll.applyOrientationTorque(Bone.UpperArmL, armL, stiffArmL, dampArmL, dt);
      this.ragdoll.applyOrientationTorque(Bone.UpperArmR, armR, stiffArmR, dampArmR, dt);
    }

    // Legs: IK-style hip orientation.
    if (!stunned) {
      this.applyHipIK('L', stiffMul, dt, sprintOn);
      this.applyHipIK('R', stiffMul, dt, sprintOn);
    }

    // Elbow & knee motor angles (revolute joints).
    // Knees keep driving normally during floppy — legs still hold the kick
    // pose. Elbows release (stiff=0) so arms flail passively with the spine.
    if (!stunned) {
      // Swimming straightens both elbows so the freestyle reach reads as
      // an extended arm instead of a bent-at-the-elbow paddle. Same -0.05
      // value the punch follow / grab reach use.
      const swimElbowL = this.isSwimming && this.swingL.phase === 'idle';
      const swimElbowR = this.isSwimming && this.swingR.phase === 'idle';
      const elbowL = this.swingL.phase === 'follow' || (grabL && this.swingL.phase === 'idle') || swimElbowL ? -0.05 : -0.45;
      const elbowR = this.swingR.phase === 'follow' || (grabR && this.swingR.phase === 'idle') || swimElbowR ? -0.05 : -0.45;
      const elbowStiff = upperLimp ? 0 : C.elbowStiff;
      this.ragdoll.setElbowAngle('L', elbowL, elbowStiff, C.elbowDamp);
      this.ragdoll.setElbowAngle('R', elbowR, elbowStiff, C.elbowDamp);
    } else {
      this.ragdoll.setElbowAngle('L', -0.55, C.elbowStiff * stiffMul, C.elbowDamp);
      this.ragdoll.setElbowAngle('R', -0.55, C.elbowStiff * stiffMul, C.elbowDamp);
    }

    // ---- Punch trigger (alternating) + swing ticking + cooldown seed ----
    // Skipped during jump-kick — you can't throw hands while free-falling.
    // Punch is now a single HELD button: each tick, if it's down, the next
    // available arm fires. Prefers the opposite side from the last punch so
    // holding Punch alternates at each arm's max cadence.
    if (!stunned && !this.jumpKickFloppy) {
      // Throw: Punch is re-purposed during a double-hand hold (controller
      // + touch use this single bind). KBM also accepts a dedicated
      // InputBtn.Throw edge (Q on keyboard) — same effect, exists so
      // trackpad players who can't chord LMB+RMB still have a release.
      // An edge press here launches the held victim up-and-forward and
      // releases both hands; no regular punch fires this tick.
      const punchEdge = btn(input, InputBtn.Punch) && !(this.prevButtons & InputBtn.Punch);
      const throwEdge = btn(input, InputBtn.Throw) && !(this.prevButtons & InputBtn.Throw);
      if ((punchEdge || throwEdge) && this.grab.isDoubleHolding()) {
        const Gc = CONFIG.character.grab;
        const fwd = { x: this._yawSin, z: this._yawCos };
        this.grab.throwDoubleHold(fwd, Gc.throwForwardImpulse, Gc.throwUpImpulse);
      }
      // Regular punch cadence: skipped while double-holding (the punch bind
      // is consumed by the throw path above).
      const doubleHolding = this.grab.isDoubleHolding();
      if (btn(input, InputBtn.Punch) && !this.staminaLocked && !doubleHolding) {
        // Metronomic cadence for a held Punch. The interval is half the
        // per-arm full cycle (windup + follow + recover + cooldown) so each
        // arm gets exactly a full cycle before it's asked to fire again,
        // and strikes land at evenly-spaced beats regardless of which arm.
        const cyclePerArmSec = (C.swingWindupMs + C.swingFollowMs + C.swingRecoverMs + C.punchCooldownMs) / 1000;
        const holdIntervalSec = cyclePerArmSec * 0.5;
        const firstPress = !(this.prevButtons & InputBtn.Punch);
        // On first press, seed the timer so the check below fires immediately.
        // Otherwise accumulate dt toward the next beat.
        this.punchHoldTimer = firstPress
          ? holdIntervalSec
          : this.punchHoldTimer + dt;

        if (this.punchHoldTimer >= holdIntervalSec) {
          // Strict alternation: next beat always fires the opposite arm —
          // UNLESS one hand is currently grabbing, in which case the free
          // hand always fires (the stuck one is busy holding on).
          let side: 'L' | 'R';
          if (this.grab.count() === 1) {
            side = this.grab.isHandStuck('L') ? 'R' : 'L';
          } else {
            side = this.lastPunchSide === 'L' ? 'R' : 'L';
          }
          const can = side === 'L'
            ? (this.swingL.phase === 'idle' && this.punchCooldownL <= 0)
            : (this.swingR.phase === 'idle' && this.punchCooldownR <= 0);
          if (can) {
            const swing = side === 'L' ? this.swingL : this.swingR;
            swing.phase = 'windup'; swing.t = 0;
            this.spendStamina(C.staminaPunchCost);
            const bone = side === 'L' ? Bone.LowerArmL : Bone.LowerArmR;
            const h = this.ragdoll.body(bone).translation();
            // Drop the event if the bone pose has gone non-finite. See the
            // footstep guard below for the same rationale — Web Audio's
            // AudioParam ramp explodes on non-finite values and wedges
            // rendering.
            if (Number.isFinite(h.x) && Number.isFinite(h.y) && Number.isFinite(h.z)) {
              this.swings.push({ characterId: this.id, side, x: h.x, y: h.y, z: h.z });
            }
            this.lastPunchSide = side;
            this.punchHoldTimer = 0;
            this.applyTorsoKickback();
            // Wriggle drain: every punch we throw while being grabbed
            // chips the bar (user mashing their way out).
            if (this.attackerCount > 0) {
              this.wriggle = Math.max(0, this.wriggle - CONFIG.character.grab.wriggleStrikeCost);
            }
          } else {
            // Arm not ready yet. Cap the timer so we retry every tick
            // without letting it grow unboundedly.
            this.punchHoldTimer = holdIntervalSec;
          }
        }
      } else {
        // Button released (or input gated): zero the cadence timer so the
        // next press fires immediately, not on some stale half-completed beat.
        this.punchHoldTimer = 0;
      }
      // Seed the per-hand cooldown the instant a swing completes.
      const preL = this.swingL.phase;
      this.tickSwing(this.swingL, dt);
      if (preL === 'recover' && this.swingL.phase === 'idle') this.punchCooldownL = C.punchCooldownMs / 1000;
      const preR = this.swingR.phase;
      this.tickSwing(this.swingR, dt);
      if (preR === 'recover' && this.swingR.phase === 'idle') this.punchCooldownR = C.punchCooldownMs / 1000;
      if (this.swingL.phase === 'follow') this.applyPunchForce('L', dt);
      if (this.swingR.phase === 'follow') this.applyPunchForce('R', dt);

      // Headbutt phase clock + sustained forward nudge on the head during
      // follow. Cooldown seeded on recover → idle.
      const preHb = this.headbuttPhase;
      this.tickHeadbutt(dt);
      if (preHb === 'recover' && this.headbuttPhase === 'idle') {
        this.headbuttCooldown = C.headbuttCooldownMs / 1000;
      }
      // No continuous force during follow — impulse-on / impulse-off gives
      // the snappy "instant accel, quick pull back" the user wanted. The
      // PD holding the head upright + the one-shot Δv pair handle it all.
    }

    // ---- Grab: each hand independent ----
    if (!stunned && !this.jumpKickFloppy) {
      const fwdX = this._yawSin;
      const fwdZ = this._yawCos;
      this.grab.update(grabL, grabR, fwdX, fwdZ, punchBtn, dt);
    } else {
      this.grab.releaseAll();
    }
    // Continuous grab drain — each attached hand costs a trickle.
    const grabsActive = this.grab.count();
    if (grabsActive > 0) this.spendStamina(grabsActive * C.staminaGrabDrainPerSec * dt);

    // ---- Surface pull-up state machine ----
    // Trigger requires Jump HELD (not edge) + double-surface grab + cooldown
    // expired + stamina available. Held-button drive is what lets a player
    // chain pull-ups against a wall to climb it: the surface re-latches after
    // the cooldown lapses, the predicate flips back true, and the wind phase
    // re-enters automatically while Jump stays down.
    if (!stunned && !this.jumpKickFloppy) {
      const Sg = CONFIG.character.surfaceGrab;
      const jumpHeld = btn(input, InputBtn.Jump);
      if (
        this.pullupPhase === 'idle'
        && jumpHeld
        && !this.staminaLocked
        && this.grab.isDoubleSurfaceHolding()
        && this.grab.surfaceLatchCooldown <= 0
      ) {
        this.pullupPhase = 'wind';
        this.pullupT = 0;
        this.spendStamina(Sg.pullupStaminaCost);
      }
      if (this.pullupPhase === 'wind') {
        // Abort cleanly if the player let go of jump or the double-surface
        // predicate broke (e.g. one hand slipped). Stamina already spent on
        // entry — that's the cost of starting.
        if (!jumpHeld || !this.grab.isDoubleSurfaceHolding()) {
          this.pullupPhase = 'idle';
          this.pullupT = 0;
        } else {
          this.pullupT += dt;
          // Pelvis is yanked UPWARD (world +y). The ragdoll's spherical
          // joints to the surfaces resist via the solver — that's exactly
          // the "grip building force" feel the user described.
          const pm = pelvis.mass();
          pelvis.applyImpulse({ x: 0, y: Sg.pullupWindAccel * pm * dt, z: 0 }, true);
          // Anchor the grabbed hand bones DOWNWARD (opposite sign to the
          // pelvis pull) so the joint constraint pre-loads instead of
          // unloading. If this sign were reversed the hands would fly with
          // the pelvis and the joints would never tension.
          for (const a of this.grab.activeGrabs()) {
            if (a.victimCharacter !== null) continue;
            const handBody = this.ragdoll.body(a.side === 'L' ? Bone.LowerArmL : Bone.LowerArmR);
            const hm = handBody.mass();
            handBody.applyImpulse({ x: 0, y: -Sg.pullupHandAnchorAccel * hm * dt, z: 0 }, true);
          }
          if (this.pullupT >= Sg.pullupWindDuration) {
            this.pullupPhase = 'release';
            this.pullupT = 0;
          }
        }
      }
      if (this.pullupPhase === 'release') {
        // Release joints + apply launch in the SAME tick so the next solver
        // step sees free bodies with fresh upward velocity. Splitting these
        // across two ticks would let the joint solver eat the launch impulse.
        for (const b of this.ragdoll.handles.bodies) {
          b.applyImpulse({ x: 0, y: Sg.pullupLaunchDv * b.mass(), z: 0 }, true);
        }
        this.grab.releaseSurfaceHolds();
        this.foot.L.state = 'air';
        this.foot.R.state = 'air';
        this.pullupPhase = 'idle';
      }
    } else if (this.pullupPhase !== 'idle') {
      this.pullupPhase = 'idle';
      this.pullupT = 0;
    }

    // Single-hand (and double-hand) drag: give the grabber extra pelvis
    // thrust along their wish direction while any hand is stuck. Feels like
    // "dragging a victim" is effortful-but-possible, without changing
    // victim mass or joint stiffness. Skipped when floppy so mid-fall
    // nothing pushes the pelvis around.
    if (grabsActive > 0 && wishMag > 0.001 && !stunned && !this.jumpKickFloppy) {
      const m = pelvis.mass();
      const thrust = CONFIG.character.grab.dragThrustPerHand * grabsActive;
      const inv = 1 / wishMag;
      pelvis.applyImpulse(
        { x: wishX * inv * thrust * m * dt, y: 0, z: wishZ * inv * thrust * m * dt },
        true
      );
    }

    // ---- Wriggle bar drain (victim-side) ----
    // Tick down the post-wriggle second-hand lock regardless of current state.
    if (this.postWriggleLock > 0) this.postWriggleLock = Math.max(0, this.postWriggleLock - dt);

    if (this.attackerCount > 0 && !stunned) {
      const G = CONFIG.character.grab;
      // Jump press — the victim trying to bounce free.
      const jumpPressed = btn(input, InputBtn.Jump) && !(this.prevButtons & InputBtn.Jump);
      if (jumpPressed) this.wriggle = Math.max(0, this.wriggle - G.wriggleJumpCost);
      // Rapid wish-vector change — mashing WASD thrashes the bar.
      const ddx = wishX - this.prevWishX;
      const ddz = wishZ - this.prevWishZ;
      const wiggleSpeed = Math.hypot(ddx, ddz) / Math.max(dt, 1e-4);
      const move = Math.min(G.wriggleMoveCapPerSec, wiggleSpeed * G.wriggleMoveK) * dt;
      if (move > 0) this.wriggle = Math.max(0, this.wriggle - move);
      // Note: punch-drain is applied at the fire site above.

      if (this.wriggle <= 0) {
        // Freed! Snap the bar back, start the second-hand lock window, and
        // rely on the attacker's Grab.update to detach when its button
        // state next reads — we proactively release via the world's
        // attacker loop by informing each character's grab. But Character
        // doesn't have a list of attackers directly; the attacker's Grab
        // tracks `heldVictims` and its `onAttackerReleased` fires when
        // joints go. For a hard release we pump a flag that all Grab
        // instances read via World. Simplest: expose a request flag and
        // have World.step() iterate characters to honor it before grab.update.
        this.wriggleEscapeRequested = true;
        this.postWriggleLock = G.postWriggleLockSec;
        this.wriggle = G.wriggleMax;
      }
    }
    this.prevWishX = wishX;
    this.prevWishZ = wishZ;

    // Stamina regen: delay after any spend; immediate if drained to zero.
    // Gated on `grounded` — feet planted AND pelvis within standing height of
    // the planted foot. Mid-air (jumping) and floating in water (no plant) do
    // not regen, otherwise the player can survive rising water indefinitely
    // by spam-jumping while bobbing.
    if (this.staminaRegenWait > 0) {
      this.staminaRegenWait = Math.max(0, this.staminaRegenWait - dt);
    }
    let regenMinPlantY = Infinity;
    if (this.foot.L.state === 'plant') regenMinPlantY = Math.min(regenMinPlantY, this.foot.L.plantY);
    if (this.foot.R.state === 'plant') regenMinPlantY = Math.min(regenMinPlantY, this.foot.R.plantY);
    const regenGrounded =
      Number.isFinite(regenMinPlantY) &&
      (pelvis.translation().y - regenMinPlantY) < C.pelvisAboveFoot + 0.15;
    if (this.staminaRegenWait <= 0 && this.stamina < 1 && regenGrounded) {
      this.stamina = Math.min(1, this.stamina + dt * C.staminaRegenPerSec);
      if (this.stamina >= 1) this.staminaLocked = false;
    }
    this.updateWedgeDetection(dt);
    this.prevButtons = input.buttons;
  }

  /** Half-second-cadence wedge check. Reads each spherical joint's two
   *  world-space anchor positions and accumulates time when they're more
   *  than WEDGE_STRETCH_THRESHOLD apart — a direct measure of "geometry is
   *  preventing the joint from closing". Cheap (6 joints × ~6 reads) and
   *  runs only every WEDGE_CHECK_INTERVAL_TICKS. */
  private updateWedgeDetection(dt: number): void {
    if (!this.wedgeAutoResolveEnabled) return;
    if (this.wedgeCooldown > 0) {
      this.wedgeCooldown = Math.max(0, this.wedgeCooldown - dt);
    }

    this.wedgeCheckTicks++;
    if (this.wedgeCheckTicks < WEDGE_CHECK_INTERVAL_TICKS) return;
    this.wedgeCheckTicks = 0;

    if (
      this.wedgeCooldown > 0 ||
      this.knockedOut ||
      this.koStunRemaining > 0 ||
      this.beingGrabbed ||
      this.grab.isGrabbing() ||
      this.jumpKickFloppy ||
      this.pullupPhase !== 'idle' ||
      this.postGrabUnlock > 0
    ) {
      this.wedgeStuckTime = 0;
      return;
    }

    // Two independent wedge signals — either one persisting for
    // WEDGE_DETECT_SEC fires the recovery:
    //   1. Joint stretch — geometry preventing a spherical joint from
    //      closing (limb threaded through a feature, body on the wrong
    //      side of where its hip wants it).
    //   2. Collider entrapment — a bone's center sits inside arena
    //      geometry. Catches the case where the joint solver still
    //      satisfies its anchor constraint (no stretch) but the bone's
    //      collider is physically lodged in a trimesh wall.
    const stretched = this.ragdoll.maxJointAnchorMismatch() > WEDGE_STRETCH_THRESHOLD;
    const buried = !stretched ? this.ragdoll.anyBoneInsideArena() : true;
    if (stretched || buried) {
      this.wedgeStuckTime += WEDGE_CHECK_INTERVAL_TICKS / CONFIG.sim.tickHz;
      if (this.wedgeStuckTime >= WEDGE_DETECT_SEC) {
        this.popToTopmostSurface();
        this.wedgeStuckTime = 0;
        this.wedgeCooldown = WEDGE_COOLDOWN_SEC;
      }
    } else {
      this.wedgeStuckTime = 0;
    }
  }

  /** Find the topmost arena surface at the current pelvis XZ via a downward
   *  raycast from high above, then translate every bone by the Y delta
   *  needed to seat the pelvis at standing height above that surface.
   *  Pose-preserving (all bones get the same delta). Velocities are zeroed
   *  so the next solver step doesn't re-launch the body. */
  private popToTopmostSurface(): void {
    const pelvis = this.ragdoll.pelvis().translation();
    const surfaceY = this.ragdoll.castArenaTopSurfaceY(pelvis.x, pelvis.z);
    if (surfaceY === null) return;
    const targetY = surfaceY + C.pelvisAboveFoot + 0.10;
    const deltaY = targetY - pelvis.y;
    if (deltaY <= 0) return;
    this.ragdoll.translateAllBonesY(deltaY);
  }

  /** Apply the spend; book-keep the lockout + regen-delay state. */
  private spendStamina(cost: number): void {
    if (cost <= 0) return;
    this.stamina = Math.max(0, this.stamina - cost);
    if (this.stamina <= 0) {
      this.staminaLocked = true;
      this.staminaRegenWait = 0; // immediate regen when drained to empty
    } else {
      this.staminaRegenWait = C.staminaRegenDelay;
    }
  }

  /** Whole-body upward Δv; flags feet airborne + refreshes jump-kick availability. */
  private doJump(): void {
    const dv = C.jumpImpulse;
    for (const b of this.ragdoll.handles.bodies) {
      b.applyImpulse({ x: 0, y: dv * b.mass(), z: 0 }, true);
    }
    this.foot.L.state = 'air';
    this.foot.R.state = 'air';
    this.jumpKickUsed = false;
    this.spendStamina(C.staminaJumpCost);
  }


  private triggerJumpKick(): void {
    this.jumpKickActive = C.jumpKickDurationMs / 1000;
    this.jumpKickUsed = true;
    this.jumpKickFloppy = true;
    this.spendStamina(C.staminaJumpKickCost);
    const p = this.ragdoll.body(Bone.Pelvis).translation();
    if (Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z)) {
      this.swings.push({ characterId: this.id, side: 'L', x: p.x, y: p.y, z: p.z });
    }
    const fwdX = this._yawSin;
    const fwdZ = this._yawCos;
    // Feet get the big forward Δv. Rest of the body gets a smaller push so
    // the whole ragdoll travels with the strike instead of leaving the torso
    // floating behind.
    const legs = [Bone.LowerLegL, Bone.LowerLegR];
    for (const bi of legs) {
      const b = this.ragdoll.body(bi);
      const m = b.mass();
      b.applyImpulse({ x: fwdX * C.jumpKickFeetDv * m, y: C.jumpKickUpDv * m, z: fwdZ * C.jumpKickFeetDv * m }, true);
    }
    const rest = [Bone.Pelvis, Bone.Torso, Bone.Head, Bone.UpperArmL, Bone.UpperArmR, Bone.LowerArmL, Bone.LowerArmR];
    for (const bi of rest) {
      const b = this.ragdoll.body(bi);
      const m = b.mass();
      b.applyImpulse({ x: fwdX * C.jumpKickBodyDv * m, y: 0, z: fwdZ * C.jumpKickBodyDv * m }, true);
    }
  }

  private triggerHeadbutt(): void {
    this.headbuttPhase = 'windup';
    this.headbuttT = 0;
    this.headbuttActive = 0;
    this.spendStamina(C.headbuttStaminaCost);
    const h = this.ragdoll.body(Bone.Head).translation();
    if (Number.isFinite(h.x) && Number.isFinite(h.y) && Number.isFinite(h.z)) {
      this.swings.push({ characterId: this.id, side: 'L', x: h.x, y: h.y, z: h.z });
    }
  }

  /** Advance the headbutt state machine. On entering 'follow' we inject a
   *  one-shot forward Δv into head/torso/pelvis (momentum through the chain
   *  so the neck doesn't snap off). 'follow' is when the head counts as a
   *  qualifying attacker in Combat. */
  private tickHeadbutt(dt: number): void {
    this.headbuttT += dt;
    switch (this.headbuttPhase) {
      case 'idle':
        return;
      case 'windup':
        if (this.headbuttT > C.headbuttWindupMs / 1000) {
          this.headbuttPhase = 'follow';
          this.headbuttT = 0;
          this.headbuttActive = C.headbuttFollowMs / 1000;
          // One-shot momentum injection. Head leads, torso follows, pelvis
          // gets a small nudge so the feet don't just get left behind.
          const fwdX = this._yawSin;
          const fwdZ = this._yawCos;
          const launch = (bi: Bone, dv: number) => {
            const b = this.ragdoll.body(bi);
            const m = b.mass();
            b.applyImpulse({ x: fwdX * dv * m, y: 0, z: fwdZ * dv * m }, true);
          };
          launch(Bone.Head, C.headbuttHeadDv);
          launch(Bone.Torso, C.headbuttTorsoDv);
          launch(Bone.Pelvis, C.headbuttPelvisDv);
        }
        return;
      case 'follow':
        this.headbuttActive = Math.max(0, this.headbuttActive - dt);
        if (this.headbuttT > C.headbuttFollowMs / 1000) {
          this.headbuttPhase = 'recover';
          this.headbuttT = 0;
          this.headbuttActive = 0;
          // Snap back: reverse the follow-entry impulse so the forward lunge
          // doesn't carry the body off its stance. Equal-and-opposite Δv per
          // bone so net forward travel from the headbutt cancels cleanly.
          const fwdX = this._yawSin;
          const fwdZ = this._yawCos;
          const pullback = (bi: Bone, dv: number) => {
            const b = this.ragdoll.body(bi);
            const m = b.mass();
            b.applyImpulse({ x: -fwdX * dv * m, y: 0, z: -fwdZ * dv * m }, true);
          };
          pullback(Bone.Head, C.headbuttHeadDv);
          pullback(Bone.Torso, C.headbuttTorsoDv);
          pullback(Bone.Pelvis, C.headbuttPelvisDv);
        }
        return;
      case 'recover':
        if (this.headbuttT > C.headbuttRecoverMs / 1000) {
          this.headbuttPhase = 'idle';
          this.headbuttT = 0;
        }
        return;
    }
  }

  /** True if ANY bone is within `maxDist` of a surface below it. Used to end
   *  jump-kick floppy mode once the character makes contact. castGroundRay
   *  filters self colliders; opponent colliders can register as ground but
   *  that's a rare edge case (kicking through an opponent) we accept. */
  private anyBoneNearGround(maxDist: number): boolean {
    for (const body of this.ragdoll.handles.bodies) {
      const t = body.translation();
      const toi = this.ragdoll.castGroundRay(t.x, t.y, t.z, maxDist);
      if (toi != null) return true;
    }
    return false;
  }

  /** Smooth the 0..1 gaitActivity envelope off real pelvis speed. gaitPhase
   *  itself is advanced continuously in updateFeet, so arm sway (which reads
   *  Math.sin(this.gaitPhase)) stays locked to the leg cycle. */
  private updateGait(dt: number): void {
    const pv = this.ragdoll.pelvis().linvel();
    const speed = Math.hypot(pv.x, pv.z);
    const moving = speed > C.footMoveThreshold;
    const target = moving ? 1 : 0;
    this.gaitActivity += (target - this.gaitActivity) * Math.min(1, dt * 5);
  }

  /** Arm world-orientation target, with per-hand punch/grab/swim/gait priority.
   *  `flayAngle` tilts the idle pose laterally outward (around local +Z) so
   *  the arm sits a little away from the torso instead of scraping against
   *  it. Ignored during punches/grabs/swim so those targets aren't diluted.
   *  `grabAim` is the "aim at cone target" quat — used when grabHeld.
   *  `swimAim` is non-null while the character is swimming → both hands
   *  flail forward in opposing alternation. */
  private armTargetPrioritised(s: SwingState, facing: Quat, grabHeld: boolean, swayAngle: number, flayAngle: number, grabAim: Quat, swimAim: Quat | null): Quat {
    switch (s.phase) {
      case 'windup':  return quatMul(facing, quatFromAxisAngle({ x: 1, y: 0, z: 0 }, +2.2));
      case 'follow':  return quatMul(facing, quatFromAxisAngle({ x: 1, y: 0, z: 0 }, -Math.PI * 0.5));
      case 'recover': return quatMul(facing, quatFromAxisAngle({ x: 1, y: 0, z: 0 }, -0.5));
    }
    // Idle priority: grab > swim > gait. Grab still wins over swim so a
    // single-hand grab attempt while swimming reaches for the surface
    // instead of flailing forward.
    if (grabHeld) return grabAim;
    if (swimAim) return swimAim;
    const flayed = quatMul(facing, quatFromAxisAngle({ x: 0, y: 0, z: 1 }, flayAngle));
    return quatMul(flayed, quatFromAxisAngle({ x: 1, y: 0, z: 0 }, 0.05 + swayAngle));
  }

  /** Build an arm-orientation quat whose +Y bone axis points from the
   *  shoulder (approximated by the torso body position) toward the current
   *  grab aim target. If no target, returns the old horizontal fallback. */
  private computeGrabArmQuat(side: 'L' | 'R', facing: Quat, liftUp: boolean): Quat {
    void side; // both arms currently share the aim origin; kept for future
    const aim = this.grab.getAimTarget();
    if (!aim) {
      // Fallback: level forward, matches pre-cone behavior.
      return quatMul(facing, quatFromAxisAngle({ x: 1, y: 0, z: 0 }, -Math.PI * 0.5));
    }
    const origin = this.ragdoll.body(Bone.Torso).translation();
    const tgt = aim.body.translation();
    let dx = tgt.x - origin.x, dy = tgt.y - origin.y, dz = tgt.z - origin.z;
    const len = Math.hypot(dx, dy, dz);
    if (len < 1e-4) return quatMul(facing, quatFromAxisAngle({ x: 1, y: 0, z: 0 }, -Math.PI * 0.5));
    dx /= len; dy /= len; dz /= len;
    // Arm bone convention: local +Y points from elbow toward the SHOULDER
    // (joint anchor is at local +Y), so the arm's free/hand end is along
    // local -Y. To aim the free end at world-direction d, we need a rotation
    // that maps local -Y → d. Factor as (Y-yaw) ∘ (X-pitch) where:
    //   yawAim rotates the local XZ frame so the target is straight ahead,
    //   then pitch = atan2(-horizLen, -dy) sends (0,-1,0) to (0, dy, horizLen).
    const yawAim = Math.atan2(dx, dz);
    const horizLen = Math.hypot(dx, dz);
    let pitch = Math.atan2(-horizLen, -dy);
    if (liftUp) {
      // Pitch sign convention: -π/2 = forward, -π = straight up. Going up
      // means DECREASING pitch. Clamp so we don't flip past vertical.
      pitch = Math.max(-Math.PI, pitch - CONFIG.character.grab.liftArmPitchRad);
    }
    return quatMul(quatFromYaw(yawAim), quatFromAxisAngle({ x: 1, y: 0, z: 0 }, pitch));
  }

  /**
   * Advance a swing's phase clock. Returns true when the swing just
   * transitioned from windup → follow (the moment the punch should "fire").
   */
  private tickSwing(s: SwingState, dt: number): boolean {
    s.t += dt;
    switch (s.phase) {
      case 'idle': return false;
      case 'windup':
        if (s.t > C.swingWindupMs / 1000) { s.phase = 'follow'; s.t = 0; return true; }
        return false;
      case 'follow':
        if (s.t > C.swingFollowMs / 1000) { s.phase = 'recover'; s.t = 0; }
        return false;
      case 'recover':
        if (s.t > C.swingRecoverMs / 1000) { s.phase = 'idle'; s.t = 0; }
        return false;
    }
    return false;
  }

  /** Sustained forward force on the punching hand during follow-through.
   * `force` × dt becomes a small per-frame impulse — joints stay intact.
   * Total impulse over the ~220ms follow phase is what gives the punch
   * its visual oomph without one frame breaking the ragdoll. */
  private applyPunchForce(side: 'L' | 'R', dt: number): void {
    // Three-phase envelope, computed in one pass:
    //   lead-out:  forward ramp + outward push (clear the torso)
    //   strike:    cosine decay of forward + inward (cross-body strike)
    //   pullback:  reduced-magnitude sine (backward + outward, plus strong down)
    const swing = side === 'L' ? this.swingL : this.swingR;
    const tRel = Math.min(1, swing.t / (C.swingFollowMs / 1000));
    const leadOut = C.punchLeadOutFrac;
    const pbStart = C.punchPullbackStart;
    const pbMul = C.punchPullbackMul;

    let f: number; // forward factor
    let iF: number; // inward factor (−1 = outward during lead-out)
    let up: number; // Y factor; sign chooses up vs down force below
    if (tRel < leadOut) {
      f = tRel / leadOut;   // ramp 0 → 1
      iF = -1;              // outward to clear the torso
      up = f;               // mild lift ramping in with the forward force
    } else if (tRel < pbStart) {
      const s = (tRel - leadOut) / (pbStart - leadOut);
      const c = Math.cos(Math.PI * 0.5 * s); // 1 → 0
      f = c;
      iF = c;
      up = c;
    } else {
      const s = (tRel - pbStart) / (1 - pbStart);
      const n = -pbMul * Math.sin(Math.PI * s); // 0 → −pbMul → 0
      f = n;
      iF = n;
      up = n;
    }

    const fwdX = this._yawSin;
    const fwdZ = this._yawCos;
    // Right-vector for the character's facing. Inward = opposite of the
    // shoulder's lateral offset: L shoulder sits at -right, so it pulls
    // toward +right; R shoulder is the opposite.
    const rightX = this._yawCos;
    const rightZ = -this._yawSin;
    const inwardSign = side === 'L' ? +1 : -1;
    const F = C.punchForce * f;
    const Fy = up >= 0 ? C.punchUpForce * up : C.punchPullDownForce * up;
    const Fin = C.punchInwardForce * inwardSign * iF;
    const dx = fwdX * F + rightX * Fin;
    const dz = fwdZ * F + rightZ * Fin;
    const lower = this.ragdoll.body(side === 'L' ? Bone.LowerArmL : Bone.LowerArmR);
    const upper = this.ragdoll.body(side === 'L' ? Bone.UpperArmL : Bone.UpperArmR);
    lower.applyImpulse({ x: dx * dt,        y: Fy * dt,        z: dz * dt }, true);
    upper.applyImpulse({ x: dx * 0.4 * dt,  y: Fy * 0.4 * dt,  z: dz * 0.4 * dt }, true);
  }

  /** One-shot backward impulse on the upper torso fired at punch trigger.
   *  Pre-empts the forward lean caused by the arm's follow-through force
   *  so the body rocks back slightly as the strike lands. */
  private applyTorsoKickback(): void {
    const k = C.punchTorsoKickback;
    if (k <= 0) return;
    const fwdX = this._yawSin;
    const fwdZ = this._yawCos;
    this.ragdoll.body(Bone.Torso).applyImpulse({ x: -fwdX * k, y: 0, z: -fwdZ * k }, true);
  }

  /**
   * Compute an upper-leg orientation PD target that keeps the leg's long
   * axis aimed from hip-anchor toward the foot target, with the knee-flex
   * axis (local +X) pointing to the character's right. Without this, the
   * spherical hip joint lets the upper leg roll around its own Y, which
   * rotates the knee-flex direction and produces splayed "crab knees"
   * during walking.
   */
  private applyHipIK(side: 'L' | 'R', stiffMul: number, dt: number, sprintOn: boolean): void {
    const pelvisT = this.ragdoll.pelvis().translation();
    const rightX = this._yawCos, rightZ = -this._yawSin;
    const sideSign = side === 'L' ? -1 : +1;
    const hipOffLocal = C.pelvisRadius * 0.55 * sideSign;
    const hipX = pelvisT.x + rightX * hipOffLocal;
    const hipY = pelvisT.y - C.pelvisHalfHeight * 0.6;
    const hipZ = pelvisT.z + rightZ * hipOffLocal;

    // Foot target world position.
    // - plant:  plantPos
    // - swing:  live arc position
    // - air:    VIRTUAL target (straight-down hang, or forward+down during
    //           jump-kick). Using the live foot position during a jump made
    //           the hip IK track the flailing leg and just reinforced the flail.
    const foot = this.foot[side];
    let fx: number, fy: number, fz: number;
    if (foot.state === 'plant') {
      // plantX/Y/Z is the live continuous-stride target (includes lift arc).
      fx = foot.plantX; fy = foot.plantY; fz = foot.plantZ;
    } else {
      const legLen = C.hipToLowerLegLength + C.lowerLegHalfHeight + C.lowerLegRadius;
      if (this.jumpKickActive > 0) {
        const fwdX = this._yawSin, fwdZ = this._yawCos;
        // Leg roughly horizontal forward + slight lift — reads as a kick.
        fx = hipX + fwdX * legLen * 0.95;
        fy = hipY - legLen * 0.2;
        fz = hipZ + fwdZ * legLen * 0.95;
      } else {
        fx = hipX;
        fy = hipY - legLen;
        fz = hipZ;
      }
    }

    // Local +Y of upper leg should point FROM foot TO hip (its +Y end is
    // the hip side). If hip and foot are nearly coincident, skip.
    const vx = hipX - fx, vy = hipY - fy, vz = hipZ - fz;
    const vlen = Math.hypot(vx, vy, vz);
    if (vlen < 0.05) return;
    const yx = vx / vlen, yy = vy / vlen, yz = vz / vlen;

    // Project character's right onto the plane perpendicular to Y.
    const rDotY = rightX * yx + rightZ * yz;
    let xxWorld = rightX - rDotY * yx;
    let xyWorld = 0     - rDotY * yy;
    let xzWorld = rightZ - rDotY * yz;
    const xlen = Math.hypot(xxWorld, xyWorld, xzWorld);
    if (xlen < 1e-3) return; // leg co-linear with right axis; degenerate
    xxWorld /= xlen; xyWorld /= xlen; xzWorld /= xlen;

    // Z = X × Y (right-handed).
    const zxWorld = xyWorld * yz - xzWorld * yy;
    const zyWorld = xzWorld * yx - xxWorld * yz;
    const zzWorld = xxWorld * yy - xyWorld * yx;

    const qTargetIK = quatFromBasis(
      { x: xxWorld, y: xyWorld, z: xzWorld },
      { x: yx, y: yy, z: yz },
      { x: zxWorld, y: zyWorld, z: zzWorld }
    );
    // Optional outward flay — small tilt around the character's forward
    // axis. L side tilts outward with −angle, R with +angle (verified at
    // yaw=0: R_z(θ) applied to +Y = (−sin θ, cos θ, 0); L leg's +Y needs to
    // drift to +X so foot end drifts to −X, which is θ<0). Only applied
    // while walking so still-stance and kicks aren't affected.
    const flayAmt = sprintOn ? C.legFlaySprintAngle : C.legFlayAngle;
    const flayTheta = flayAmt * sideSign * this.gaitActivity;
    let qTarget = qTargetIK;
    if (Math.abs(flayTheta) > 1e-4) {
      const fwdAx = { x: this._yawSin, y: 0, z: this._yawCos };
      const flayQuat = quatFromAxisAngle(fwdAx, flayTheta);
      qTarget = quatMul(flayQuat, qTargetIK);
    }

    const bone = side === 'L' ? Bone.LowerLegL : Bone.LowerLegR;
    // Airborne legs get their own PD gains — we really need them to hold the
    // straight-down (or kick-forward) pose against joint drag, since there's
    // no ground anchor to help.
    const airborne = foot.state === 'air' || this.jumpKickActive > 0;
    const kp = airborne ? C.airLegHipK : C.hipIkStiff * stiffMul;
    const kd = airborne ? C.airLegHipD : C.hipIkDamp;
    this.ragdoll.applyOrientationTorque(bone, qTarget, kp, kd, dt);
  }

  /**
   * Continuous-stride foot controller.
   *
   * Phase (this.gaitPhase) advances by (speed / (2·footGaitStride)) · 2π
   * each tick — i.e. one half-cycle per `footGaitStride` metres of pelvis
   * travel, so stride tempo is bound to actual movement speed. No phase
   * advance when below `footMoveThreshold` — amplitude (gaitActivity)
   * smoothly fades the feet back to REST.
   *
   *   L offset (fwd) = -cos(phase)     · footSlotOffset · amp
   *   R offset (fwd) = -cos(phase + π) · footSlotOffset · amp
   *   L lift        = stepLiftHeight · max(0, sin(phase))²     · amp
   *   R lift        = stepLiftHeight · max(0, sin(phase + π))² · amp
   *
   * Footstep audio fires on the falling edge of lift (foot returning to
   * ground) with a per-foot cooldown to suppress jitter.
   *
   * Airborne: `foot.state === 'air'` (set by doJump / knock-offs) skips the
   * spring entirely so the joint + gravity own the jump arc. Landing is
   * detected by a short raycast off the foot body.
   */
  private updateFeet(dt: number): void {
    const pelvis = this.ragdoll.pelvis();
    const pt = pelvis.translation();
    const pv = pelvis.linvel();
    const speed = Math.hypot(pv.x, pv.z);
    const moving = speed > C.footMoveThreshold;

    const fwdX = this._yawSin, fwdZ = this._yawCos;
    const rightX = this._yawCos, rightZ = -this._yawSin;

    if (moving) {
      // Full cycle = 2 × footGaitStride of pelvis travel. Advance phase
      // proportionally so the stride visually tempos the actual movement.
      const cycleDist = C.footGaitStride * 2;
      const TWO_PI = Math.PI * 2;
      this.gaitPhase += (speed * dt / cycleDist) * TWO_PI;
      while (this.gaitPhase >= TWO_PI) this.gaitPhase -= TWO_PI;
      while (this.gaitPhase < 0) this.gaitPhase += TWO_PI;
    }

    this.updateFootTarget(this.foot.L, -1, this.gaitPhase, pt.x, pt.y, pt.z, fwdX, fwdZ, rightX, rightZ, dt);
    this.updateFootTarget(this.foot.R, +1, this.gaitPhase + Math.PI, pt.x, pt.y, pt.z, fwdX, fwdZ, rightX, rightZ, dt);
  }

  /** Spring the foot to its continuous pelvis-relative target.
   *  Skips the spring while airborne so the jump arc isn't cut short. */
  private updateFootTarget(
    foot: Foot,
    sideSign: number,
    phase: number,
    px: number, py: number, pz: number,
    fwdX: number, fwdZ: number,
    rightX: number, rightZ: number,
    dt: number
  ): void {
    const body = this.ragdoll.body(foot.side === 'L' ? Bone.LowerLegL : Bone.LowerLegR);

    if (foot.stepCooldown > 0) foot.stepCooldown = Math.max(0, foot.stepCooldown - dt);

    // --- Airborne: let joint + gravity drive the foot. ---
    if (foot.state === 'air') {
      const bp = body.translation();
      const bv = body.linvel();
      const hit = this.ragdoll.castGroundRay(bp.x, bp.y + 0.05, bp.z, C.footLandProbeDist);
      const touched = hit != null && bv.y <= C.footLandDownVy;
      if (touched) {
        foot.state = 'plant';
        foot.prevLift = 0;
      } else {
        foot.plantX = bp.x; foot.plantY = bp.y; foot.plantZ = bp.z;
        return;
      }
    }

    // --- Grounded: continuous offset target. ---

    const amp = this.gaitActivity; // 0 = rest, 1 = full stride
    const fwdOff = -Math.cos(phase) * C.footSlotOffset * amp;
    const rightOff = sideSign * C.stanceWidth;
    const tgtX = px + fwdX * fwdOff + rightX * rightOff;
    const tgtZ = pz + fwdZ * fwdOff + rightZ * rightOff;

    // Cast from just above the pelvis (≈ torso bottom), NOT from a fixed
    // high Y. With a high origin any overhead geometry — a low ceiling, a
    // raised tutorial divider, an overhanging beam — is the first hit going
    // down and gets treated as the foot's "ground", which pins the foot
    // plant up there and the altitude PD then magnetises the whole body up
    // to the ceiling. Origin clamped to py + 0.3 covers any legit step-up
    // (knee-height stairs at most) while geometry above the torso can never
    // intercept the ray.
    const rayY = py + 0.3;
    const hit = this.ragdoll.castGroundRayWithBody(tgtX, rayY, tgtZ, rayY + C.footFindMaxDist);
    if (hit == null) {
      // Slot target is over the void — foot can't plant. Flip to air so the
      // pelvis altitude PD falls back to its own void probe (and stops
      // treating a phantom ground height as a valid plant, which would
      // float the character off-edge indefinitely).
      const bp = body.translation();
      foot.state = 'air';
      foot.plantX = bp.x; foot.plantY = bp.y; foot.plantZ = bp.z;
      foot.prevLift = 0;
      return;
    }
    const baseY = (rayY - hit.toi) + C.footCenterHeight;

    // Lift only during the forward-swing half of the cycle. Amplitude so
    // idle characters don't bob.
    const liftRaw = Math.max(0, Math.sin(phase));
    const lift = C.stepLiftHeight * liftRaw * liftRaw * amp;
    const tgtY = baseY + lift;

    // Footstep event on the landing edge — lift falls through this threshold.
    const LIFT_LAND_EPS = 0.015;
    if (foot.prevLift > LIFT_LAND_EPS && lift <= LIFT_LAND_EPS && foot.stepCooldown <= 0) {
      const fy = baseY - C.footCenterHeight;
      // Skip the event if any coord is non-finite. The consumer feeds these
      // straight into a PositionalAudio position which then gets passed to
      // Web Audio's linearRampToValueAtTime — one NaN there wedges the
      // entire render loop. Observed once on the Saturn map after a
      // character's foot resolved over a degenerate trimesh contact; cheaper
      // to drop the event than to audit every upstream math source.
      if (Number.isFinite(tgtX) && Number.isFinite(fy) && Number.isFinite(tgtZ)) {
        this.footsteps.push({
          characterId: this.id,
          side: foot.side,
          x: tgtX,
          y: fy,
          z: tgtZ
        });
      }
      foot.stepCooldown = 0.08;
    }
    foot.prevLift = lift;

    foot.plantX = tgtX;
    foot.plantY = tgtY;
    foot.plantZ = tgtZ;

    this.applyFootSpring(body, tgtX, tgtY, tgtZ, C.footTargetK, C.footTargetD, dt);
  }

  /**
   * Horizontal spring from the pelvis toward the midpoint of the active
   * foot support — planted feet AT their plantPos, swinging feet at their
   * CURRENT arc position. Including the swing is what produces the
   * "body-drag" feel: as the swing foot arcs forward, the midpoint slides
   * forward, pulling the pelvis through the stride so the planted foot
   * ends up clearly behind the hip. If we only used plants, the target
   * snapped back to the stance foot every step and the pelvis never
   * passed over it.
   *
   * Both feet airborne → no target; pelvis drifts free (jump / knockdown).
   */
  private applyPelvisFollow(wishX: number, wishZ: number, wishMag: number, sprintOn: boolean, dt: number): void {
    const pelvis = this.ragdoll.pelvis();

    if (C.useFootDrivenHip) {
      // LEGACY: foot-driven pelvis. Horizontally spring the hip to the
      // midpoint of active foot support (planted feet + live swing arcs).
      let cx = 0, cz = 0, n = 0;
      for (const side of ['L', 'R'] as const) {
        const foot = this.foot[side];
        if (foot.state === 'plant') {
          cx += foot.plantX; cz += foot.plantZ; n++;
        }
      }
      if (n === 0) return;
      cx /= n; cz /= n;
      const pt = pelvis.translation();
      const pv = pelvis.linvel();
      const kp = C.pelvisFollowK, kd = C.pelvisFollowD;
      const fx = (cx - pt.x) * kp - pv.x * kd;
      const fz = (cz - pt.z) * kp - pv.z * kd;
      const m = pelvis.mass();
      pelvis.applyImpulse({ x: fx * m * dt, y: 0, z: fz * m * dt }, true);
      return;
    }

    // NEW: input-driven hip float. Two regimes:
    //   MOVING (wishMag > 0.08): converge pelvis linvel to wishDir × speed.
    //   IDLE:                    position-hold spring on world XZ so gravity
    //                            along a ship tilt can't slide us away.
    // Skip while both feet airborne; airControlAccel already handles the
    // mid-air nudge and we don't want to yank velocity to zero mid-jump.
    const anyPlanted = this.foot.L.state === 'plant' || this.foot.R.state === 'plant';
    if (!anyPlanted) {
      this.hipPlantX = null; // re-anchor when we next touch down + go idle
      return;
    }

    const pt = pelvis.translation();
    const pv = pelvis.linvel();
    const m = pelvis.mass();

    if (wishMag > 0.08) {
      // MOVING: velocity convergence, clear the idle anchor.
      this.hipPlantX = null;
      const speed = sprintOn ? C.hipSprintSpeed : C.hipWalkSpeed;
      const inv = 1 / wishMag;
      const scale = Math.min(1, wishMag) * speed;
      const tvx = wishX * inv * scale;
      const tvz = wishZ * inv * scale;
      const k = C.hipAccel;
      const fx = (tvx - pv.x) * k * m;
      const fz = (tvz - pv.z) * k * m;
      pelvis.applyImpulse({ x: fx * dt, y: 0, z: fz * dt }, true);
      return;
    }

    // IDLE: lock to a world position. Capture the anchor on the first idle
    // tick after moving. Once anchored, a critically-damped PD holds the
    // pelvis in place — gravity along a slope is cancelled out by the spring.
    if (this.hipPlantX == null) {
      this.hipPlantX = pt.x;
      this.hipPlantZ = pt.z;
    }
    const kp = C.hipPlantK, kd = C.hipPlantD;
    const fx = (this.hipPlantX - pt.x) * kp - pv.x * kd;
    const fz = (this.hipPlantZ - pt.z) * kp - pv.z * kd;
    pelvis.applyImpulse({ x: fx * m * dt, y: 0, z: fz * m * dt }, true);
  }

  /** Drop the IDLE pelvis anchor. Call this from any external system that
   *  position-warps the character (conveyor belts, wind shoves, teleports)
   *  so applyPelvisFollow's IDLE PD spring re-anchors at the new location
   *  next tick instead of dragging the pelvis back to where it was when
   *  the character last stopped moving. */
  clearIdleAnchor(): void {
    this.hipPlantX = null;
  }

  /** Spring-damper toward (tx, ty, tz) applied as a per-frame impulse. */
  private applyFootSpring(body: RAPIER.RigidBody, tx: number, ty: number, tz: number, kp: number, kd: number, dt: number): void {
    const p = body.translation();
    const v = body.linvel();
    const m = body.mass();
    const fx = (tx - p.x) * kp - v.x * kd;
    const fy = (ty - p.y) * kp - v.y * kd;
    const fz = (tz - p.z) * kp - v.z * kd;
    body.applyImpulse({ x: fx * m * dt, y: fy * m * dt, z: fz * m * dt }, true);
  }

  /**
   * Register a qualifying hit. Combat has already filtered (attacker bone,
   * force, alignment, accuracy floor). `attackerBone` is used to apply the
   * head-hit bonus to the KO chip curve.
   *
   * Two bars:
   * - HP (long-term, 10 KOs to deplete): every hit chips a small amount
   *   scaled by accuracy; a full KO additionally charges the remainder of
   *   HP_max / 10.
   * - KO (wakeness, regens): chip = lerp(koChipMin, koChipMax, quality^exp),
   *   where quality = accuracy × forceScalar × (headBonus if head). At 0,
   *   the character flops for koStunSec, then stands back up with a full bar.
   *
   * Re-KO immunity: while koStunRemaining > 0 or koGracePostStand > 0 we
   * still apply the knockback impulse (piling-on shoves the body visibly)
   * but deal no HP/KO damage.
   */
  takeImpact(strength: number, accuracy: number, attackerBone: Bone, vicBone: Bone, fromX: number, fromZ: number): void {
    if (this.wasted) return;
    const immune = this.koStunRemaining > 0 || this.koGracePostStand > 0;

    if (!immune) {
      // --- HP chip (every hit) ---
      const accForHp = accuracy >= C.hpAccuracyThreshold ? 1.0 : accuracy / C.hpAccuracyThreshold;
      const hpChunkPerKO = C.hpMax * C.hpKOChunk;
      const hpChip = hpChunkPerKO * C.hpChipFractionOfKO * accForHp;
      this.hp = Math.max(0, this.hp - hpChip);

      // --- KO chip (curve) ---
      const forceScalar = Math.min(1.5, strength / C.koChipForceRef);
      const headBonus = attackerBone === Bone.Head ? C.koChipHeadBonus : 1.0;
      const quality = Math.min(1, accuracy * forceScalar * headBonus);
      const chip = C.koChipMin + (C.koChipMax - C.koChipMin) * Math.pow(quality, C.koChipExponent);
      this.ko = Math.max(0, this.ko - chip);
      this.koRegenWait = C.koRegenDelay;

      // --- KO trigger: charge the rest of the HP-per-KO chunk ---
      if (this.ko <= 0) {
        this.knockedOut = true;
        this.koStunRemaining = C.koStunSec;
        const hpChipAlreadyTaken = hpChunkPerKO * C.hpChipFractionOfKO * accForHp;
        this.hp = Math.max(0, this.hp - (hpChunkPerKO - hpChipAlreadyTaken));
      }
      if (this.hp <= 0) this.wasted = true;
    }

    // --- Knockback impulse + stun always apply (even during immune window) ---
    this.stunRemaining = Math.max(this.stunRemaining, (C.stunMsPerImpact / 1000) * Math.min(2.0, strength * 0.18));
    const body = this.ragdoll.body(vicBone);
    const t = body.translation();
    const dx = t.x - fromX, dz = t.z - fromZ;
    const len = Math.hypot(dx, dz) + 1e-3;
    const k = Math.min(strength * C.impactImpulseScale, 18);
    body.applyImpulse({
      x: (dx / len) * k * body.mass(),
      y: 0.6 * k * body.mass(),
      z: (dz / len) * k * body.mass()
    }, true);
  }

  reset(spawnX: number, spawnZ: number, yaw: number, spawnY?: number): void {
    this.hp = C.hpMax;
    this.ko = C.koMax;
    this.koStunRemaining = 0;
    this.koRegenWait = 0;
    this.koGracePostStand = 0;
    this.wasted = false;
    this.stamina = 1;
    this.staminaRegenWait = 0;
    this.staminaLocked = false;
    this.stunRemaining = 0;
    this.knockedOut = false;
    this.ringedOut = false;
    this.setTargetYaw(yaw);
    this.prevButtons = 0;
    this.swingL = { phase: 'idle', t: 0, side: 'L' };
    this.swingR = { phase: 'idle', t: 0, side: 'R' };
    this.headbuttPhase = 'idle';
    this.headbuttT = 0;
    this.headbuttActive = 0;
    this.headbuttCooldown = 0;
    this.hipPlantX = null;
    this.punchCooldownL = 0;
    this.punchCooldownR = 0;
    this.jumpKickActive = 0;
    this.jumpKickUsed = false;
    this.jumpKickFloppy = false;
    this.gaitPhase = 0;
    this.gaitActivity = 0;
    this.headKickPrevDirX = 0;
    this.headKickPrevDirZ = 0;
    this.wedgeCheckTicks = 0;
    this.wedgeStuckTime = 0;
    this.wedgeCooldown = 0;

    // Reset every bone to its hierarchically-derived spawn pose (joints align exactly).
    const positions = Ragdoll.computeSpawnPositions(spawnX, spawnZ, yaw, spawnY);
    const yawQ = quatFromYaw(yaw);
    const bones = this.ragdoll.handles.bodies;
    for (let i = 0; i < bones.length; i++) {
      const b = bones[i]!;
      const p = positions[i]!;
      b.setTranslation({ x: p[0], y: p[1], z: p[2] }, true);
      b.setRotation(yawQ, true);
      b.setLinvel({ x: 0, y: 0, z: 0 }, true);
      b.setAngvel({ x: 0, y: 0, z: 0 }, true);
    }
    this.plantFeetHere();
    this.grab.releaseAll();
    this.wriggle = CONFIG.character.grab.wriggleMax;
    this.postWriggleLock = 0;
    this.attackerCount = 0;
    this.prevWishX = 0;
    this.prevWishZ = 0;
    this.wriggleEscapeRequested = false;
  }

  writeSnapshot(snap: CharacterSnapshot): void {
    this.ragdoll.writeBones(snap.bones);
    snap.hp = this.hp;
    snap.ko = this.ko;
    snap.koStunRemaining = this.koStunRemaining;
    snap.wasted = this.wasted;
    snap.stamina = this.stamina;
    snap.staminaLocked = this.staminaLocked;
    snap.stunRemaining = this.stunRemaining;
    snap.knockedOut = this.knockedOut;
    snap.ringedOut = this.ringedOut;
    const t = this.ragdoll.pelvis().translation();
    snap.pelvisX = t.x; snap.pelvisY = t.y; snap.pelvisZ = t.z;
    snap.facingYaw = yawFromQuat(this.ragdoll.pelvis().rotation());
    snap.swingPhaseL = swingToFloat(this.swingL);
    snap.swingPhaseR = swingToFloat(this.swingR);
    snap.grabbing = this.grab.isGrabbing();
    snap.doubleHolding = this.grab.isDoubleHolding();
    snap.wriggle = this.wriggle;
    snap.beingGrabbed = this.attackerCount > 0;
    snap.bothAir = this.foot.L.state === 'air' && this.foot.R.state === 'air';
    snap.isSwimming = this.isSwimming;
  }

  static makeSnapshot(id: number): CharacterSnapshot {
    return emptySnapshot(id, BONE_COUNT);
  }

  /** Serialize every field that `step()` reads or writes into a plain struct.
   *  Paired with `loadInternalState()` for rollback replay in Phase 1.5 of
   *  the netcode evolution: on a divergence we restore the Ragdoll bodies
   *  to the server's state, then restore THIS Character's scalar state to
   *  what it was at the ack tick, then replay un-acked inputs by stepping
   *  the world once per buffered InputFrame.
   *
   *  Excluded: `ragdoll` (owns its own physics state — snapshot via
   *  `writeBonesFull`), `grab` (no opponents in the online-client's
   *  single-character world so grab state is trivially always empty), and
   *  `id` (immutable).
   *
   *  Pass an existing `out` object to avoid per-tick allocations. */
  saveInternalState(out?: CharacterInternalState): CharacterInternalState {
    const dst = out ?? makeEmptyInternalState();
    dst.swingL.phase = this.swingL.phase;
    dst.swingL.t = this.swingL.t;
    dst.swingL.side = this.swingL.side;
    dst.swingR.phase = this.swingR.phase;
    dst.swingR.t = this.swingR.t;
    dst.swingR.side = this.swingR.side;
    dst.headbuttPhase = this.headbuttPhase;
    dst.headbuttActive = this.headbuttActive;
    dst.headbuttT = this.headbuttT;
    dst.headbuttCooldown = this.headbuttCooldown;
    dst.targetYaw = this.targetYaw;
    dst.prevButtons = this.prevButtons;
    dst.punchCooldownL = this.punchCooldownL;
    dst.punchCooldownR = this.punchCooldownR;
    dst.lastPunchSide = this.lastPunchSide;
    dst.punchHoldTimer = this.punchHoldTimer;
    dst.hipPlantX = this.hipPlantX;
    dst.hipPlantZ = this.hipPlantZ;
    copyFootInto(dst.footL, this.foot.L);
    copyFootInto(dst.footR, this.foot.R);
    dst.gaitPhase = this.gaitPhase;
    dst.gaitActivity = this.gaitActivity;
    dst.headKickPrevDirX = this.headKickPrevDirX;
    dst.headKickPrevDirZ = this.headKickPrevDirZ;
    dst.jumpKickActive = this.jumpKickActive;
    dst.jumpKickUsed = this.jumpKickUsed;
    dst.jumpKickFloppy = this.jumpKickFloppy;
    dst.hp = this.hp;
    dst.ko = this.ko;
    dst.koStunRemaining = this.koStunRemaining;
    dst.koRegenWait = this.koRegenWait;
    dst.koGracePostStand = this.koGracePostStand;
    dst.wasted = this.wasted;
    dst.ringedOut = this.ringedOut;
    dst.knockedOut = this.knockedOut;
    dst.stamina = this.stamina;
    dst.staminaRegenWait = this.staminaRegenWait;
    dst.staminaLocked = this.staminaLocked;
    dst.stunRemaining = this.stunRemaining;
    dst.wriggle = this.wriggle;
    dst.postWriggleLock = this.postWriggleLock;
    dst.attackerCount = this.attackerCount;
    dst.postGrabUnlock = this.postGrabUnlock;
    dst.prevWishX = this.prevWishX;
    dst.prevWishZ = this.prevWishZ;
    dst.wriggleEscapeRequested = this.wriggleEscapeRequested;
    dst.wedgeCheckTicks = this.wedgeCheckTicks;
    dst.wedgeStuckTime = this.wedgeStuckTime;
    dst.wedgeCooldown = this.wedgeCooldown;
    return dst;
  }

  /** Restore every field previously captured by `saveInternalState()`. */
  loadInternalState(src: CharacterInternalState): void {
    this.swingL.phase = src.swingL.phase;
    this.swingL.t = src.swingL.t;
    this.swingL.side = src.swingL.side;
    this.swingR.phase = src.swingR.phase;
    this.swingR.t = src.swingR.t;
    this.swingR.side = src.swingR.side;
    this.headbuttPhase = src.headbuttPhase;
    this.headbuttActive = src.headbuttActive;
    this.headbuttT = src.headbuttT;
    this.headbuttCooldown = src.headbuttCooldown;
    this.setTargetYaw(src.targetYaw);
    this.prevButtons = src.prevButtons;
    this.punchCooldownL = src.punchCooldownL;
    this.punchCooldownR = src.punchCooldownR;
    this.lastPunchSide = src.lastPunchSide;
    this.punchHoldTimer = src.punchHoldTimer;
    this.hipPlantX = src.hipPlantX;
    this.hipPlantZ = src.hipPlantZ;
    copyFootInto(this.foot.L, src.footL);
    copyFootInto(this.foot.R, src.footR);
    this.gaitPhase = src.gaitPhase;
    this.gaitActivity = src.gaitActivity;
    this.headKickPrevDirX = src.headKickPrevDirX;
    this.headKickPrevDirZ = src.headKickPrevDirZ;
    this.jumpKickActive = src.jumpKickActive;
    this.jumpKickUsed = src.jumpKickUsed;
    this.jumpKickFloppy = src.jumpKickFloppy;
    this.hp = src.hp;
    this.ko = src.ko;
    this.koStunRemaining = src.koStunRemaining;
    this.koRegenWait = src.koRegenWait;
    this.koGracePostStand = src.koGracePostStand;
    this.wasted = src.wasted;
    this.ringedOut = src.ringedOut;
    this.knockedOut = src.knockedOut;
    this.stamina = src.stamina;
    this.staminaRegenWait = src.staminaRegenWait;
    this.staminaLocked = src.staminaLocked;
    this.stunRemaining = src.stunRemaining;
    this.wriggle = src.wriggle;
    this.postWriggleLock = src.postWriggleLock;
    this.attackerCount = src.attackerCount;
    this.postGrabUnlock = src.postGrabUnlock;
    this.prevWishX = src.prevWishX;
    this.prevWishZ = src.prevWishZ;
    this.wriggleEscapeRequested = src.wriggleEscapeRequested;
    this.wedgeCheckTicks = src.wedgeCheckTicks;
    this.wedgeStuckTime = src.wedgeStuckTime;
    this.wedgeCooldown = src.wedgeCooldown;
  }
}

/** Plain-old-data mirror of every `Character` scalar field that affects
 *  `step()` behavior. Populated by `Character.saveInternalState()` and
 *  restored by `Character.loadInternalState()` for rollback replay. */
export interface CharacterInternalState {
  swingL: SwingState;
  swingR: SwingState;
  headbuttPhase: 'idle' | 'windup' | 'follow' | 'recover';
  headbuttActive: number;
  headbuttT: number;
  headbuttCooldown: number;
  targetYaw: number;
  prevButtons: number;
  punchCooldownL: number;
  punchCooldownR: number;
  lastPunchSide: 'L' | 'R';
  punchHoldTimer: number;
  hipPlantX: number | null;
  hipPlantZ: number;
  footL: Foot;
  footR: Foot;
  gaitPhase: number;
  gaitActivity: number;
  headKickPrevDirX: number;
  headKickPrevDirZ: number;
  jumpKickActive: number;
  jumpKickUsed: boolean;
  jumpKickFloppy: boolean;
  hp: number;
  ko: number;
  koStunRemaining: number;
  koRegenWait: number;
  koGracePostStand: number;
  wasted: boolean;
  ringedOut: boolean;
  knockedOut: boolean;
  stamina: number;
  staminaRegenWait: number;
  staminaLocked: boolean;
  stunRemaining: number;
  wriggle: number;
  postWriggleLock: number;
  attackerCount: number;
  postGrabUnlock: number;
  prevWishX: number;
  prevWishZ: number;
  wriggleEscapeRequested: boolean;
  wedgeCheckTicks: number;
  wedgeStuckTime: number;
  wedgeCooldown: number;
}

/** Factory for a fresh `CharacterInternalState` pre-allocated with nested
 *  sub-struct instances so `saveInternalState(out)` can reuse the same
 *  object across ticks without per-field allocation. */
export function makeEmptyInternalState(): CharacterInternalState {
  return {
    swingL: { phase: 'idle', t: 0, side: 'L' },
    swingR: { phase: 'idle', t: 0, side: 'R' },
    headbuttPhase: 'idle',
    headbuttActive: 0,
    headbuttT: 0,
    headbuttCooldown: 0,
    targetYaw: 0,
    prevButtons: 0,
    punchCooldownL: 0,
    punchCooldownR: 0,
    lastPunchSide: 'R',
    punchHoldTimer: 0,
    hipPlantX: null,
    hipPlantZ: 0,
    footL: makeFoot('L'),
    footR: makeFoot('R'),
    gaitPhase: 0,
    gaitActivity: 0,
    headKickPrevDirX: 0,
    headKickPrevDirZ: 0,
    jumpKickActive: 0,
    jumpKickUsed: false,
    jumpKickFloppy: false,
    hp: C.hpMax,
    ko: C.koMax,
    koStunRemaining: 0,
    koRegenWait: 0,
    koGracePostStand: 0,
    wasted: false,
    ringedOut: false,
    knockedOut: false,
    stamina: 1,
    staminaRegenWait: 0,
    staminaLocked: false,
    stunRemaining: 0,
    wriggle: CONFIG.character.grab.wriggleMax,
    postWriggleLock: 0,
    attackerCount: 0,
    postGrabUnlock: 0,
    prevWishX: 0,
    prevWishZ: 0,
    wriggleEscapeRequested: false,
    wedgeCheckTicks: 0,
    wedgeStuckTime: 0,
    wedgeCooldown: 0
  };
}

function copyFootInto(dst: Foot, src: Foot): void {
  dst.side = src.side;
  dst.state = src.state;
  dst.plantX = src.plantX;
  dst.plantY = src.plantY;
  dst.plantZ = src.plantZ;
  dst.prevLift = src.prevLift;
  dst.stepCooldown = src.stepCooldown;
}

void QUAT_ID;

function makeFoot(side: 'L' | 'R'): Foot {
  return {
    side,
    state: 'plant',
    plantX: 0, plantY: 0, plantZ: 0,
    prevLift: 0,
    stepCooldown: 0
  };
}

function isSwingActive(s: SwingState): boolean {
  return s.phase === 'windup' || s.phase === 'follow';
}

function swingToFloat(s: SwingState): number {
  switch (s.phase) {
    case 'idle': return 0;
    case 'windup': return 0.5;
    case 'follow': return 1.5;
    case 'recover': return 2.5;
  }
}
function clamp(v: number, lo: number, hi: number): number { return Math.max(lo, Math.min(hi, v)); }
function wrapAngle(a: number): number {
  while (a > Math.PI) a -= 2 * Math.PI;
  while (a < -Math.PI) a += 2 * Math.PI;
  return a;
}
function yawFromQuat(q: Quat): number {
  const fx = 2 * (q.x * q.z + q.w * q.y);
  const fz = 1 - 2 * (q.x * q.x + q.y * q.y);
  return Math.atan2(fx, fz);
}
